import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Grid, 
  Button, 
  ButtonGroup,
  Container,
  TextField,
  InputAdornment,
  Alert,
  IconButton,
  Tooltip,
  styled,
  Chip,
  GlobalStyles,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArticleIcon from '@mui/icons-material/Article';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled as muiStyled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getBranches as getBranchesAdapter } from '../../../services/migration/branchDataService';
import firebaseDataService from '../../../services/FirebaseDataService';
// ✅ Artık backend'de şifreleme yapılıyor, frontend'de decrypt etmeye gerek yok
import { ACCOUNTING_API, EINVOICE_API } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { AppDatePicker } from '../../common/AppDatePicker';
import BusinessIcon from '@mui/icons-material/Business';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EInvoiceIntegrationModal from './EInvoiceIntegrationModal';
import XsltTemplateModal from './XsltTemplateModal';
import PaletteIcon from '@mui/icons-material/Palette';
import EInvoiceDetailModal from './EInvoiceDetailModal';
import EInvoiceUserQueryModal from './EInvoiceUserQueryModal';
import EInvoiceStatusQueryModal from './EInvoiceStatusQueryModal';
import EInvoiceHistoryModal from './EInvoiceHistoryModal';
import InvoiceResponseModal from './InvoiceResponseModal';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import CompanyDetailsModal from '../invoice/CompanyDetailsModal';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Başlık alanı için styled component
const HeaderContainer = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// E-Fatura durumları
const EINVOICE_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  DELIVERED: 'delivered',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled'
};

// E-Fatura arayüzü (API'den gelen veri formatı)
// Gelen E-Fatura veri tipi (API'den gelen veri formatına göre güncellendi)
interface GelenEInvoiceData {
  // belge (base)
  belgeNo: string;
  belgeSiraNo: string;
  belgeTarihi: string;
  belgeTuru: string;
  ettn: string;
  gonderenEtiket: string;
  gonderenVknTckn: string;
  
  // belgev2
  alanEtiket?: string;
  aliciUnvan: string;
  saticiUnvan: string;
  belgeVersiyon: string;
  subeKodu?: string;
  zarfId: string;
  ekBilgiler: any;
  
  // belgev3 - ✅ TOPLAM TUTAR!
  odenecekTutar?: string;
  odenecekTutarDovizCinsi?: string;
  
  // belgev4
  arsivlenmis?: string;
  belgeHash?: string;
  
  // belgev5
  faturaGelisTarihi?: string;
  
  // belgev6 (QNB max: 6.0)
  profileId?: string;
}

// Giden E-Fatura veri tipi (yeni format)
interface GidenEInvoiceData {
  belgeOid: string;
  ettn: string | null;
  yerelBelgeNo: string;
  alimZamani: string; // "14/05/2025 16:21:43" formatında
  alimDurumu: string; // "İşlendi", "İşleme Hatası" vs.
  hataMesaji: string | null;
  islemYapanVkn: string | null;
  kullaniciKodu: string;
  kaynak: string;
  // ✅ Zengin liste alanları (EDM giden kutusu başlıklarından)
  aliciUnvan?: string;
  odenecekTutar?: string;
  odenecekTutarDovizCinsi?: string;
  profileId?: string;
}

// Gelen E-Fatura API Response tipi
interface GelenEInvoiceApiResponse {
  toplamBelge: number;
  belgeler: GelenEInvoiceData[];
}

// Giden E-Fatura API Response tipi
interface GidenEInvoiceApiResponse {
  toplam: number;
  belgeler: GidenEInvoiceData[];
}

// Birleşik tip (tabloda kullanım için)
type EInvoiceData = GelenEInvoiceData | GidenEInvoiceData;

// API Response arayüzü (geriye uyumluluk için)
interface EInvoiceApiResponse {
  toplamBelge: number;
  belgeler: GelenEInvoiceData[];
}

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  description?: string;
}

interface EInvoicePageProps {
  isManager?: boolean;
  managerBranchId?: string | null;
}

const EInvoice: React.FC<EInvoicePageProps> = ({ isManager = false, managerBranchId }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [gelenEInvoices, setGelenEInvoices] = useState<GelenEInvoiceData[]>([]);
  const [filteredGelenEInvoices, setFilteredGelenEInvoices] = useState<GelenEInvoiceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingExport, setLoadingExport] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Aktif e-fatura entegratörü (gelen kutusu kaynağını belirler): 'edm' | 'finansbank' | null
  const [eInvoiceProvider, setEInvoiceProvider] = useState<string | null>(null);

  // EDM kalan kontör (sağ üstte gösterilir; yalnızca EDM entegrasyonunda anlamlı)
  const [kontorLeft, setKontorLeft] = useState<number | null>(null);
  const [kontorLoading, setKontorLoading] = useState(false);
  const [kontorError, setKontorError] = useState<string | null>(null);
  // Kontör göstergesi için EDM tespiti: gelen kutusu akışından bağımsız olarak
  // einvoice API'nin environment ucundan da çözülür (sayfa gelen listesini
  // yüklemeden önce/başarısız olsa bile kontör görünebilsin).
  const [kontorProviderIsEdm, setKontorProviderIsEdm] = useState(false);
  
  // Sube filtresi state'leri
  const [branchMap, setBranchMap] = useState<Record<string, string>>({});
  const [branchFilter, setBranchFilter] = useState<string>(isManager && managerBranchId ? managerBranchId : 'all');
  const [pavoPayBranchMap, setPavoPayBranchMap] = useState<Record<string, string>>({});
  const [hasGlobalPavoPayIntegration, setHasGlobalPavoPayIntegration] = useState(false);
  const [pavoPayBranchWarningOpen, setPavoPayBranchWarningOpen] = useState(false);

  // Entegrasyon modalı için state'ler
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);

  // Tasarım şablonları (XSLT) modalı
  const [isXsltTemplateModalOpen, setIsXsltTemplateModalOpen] = useState(false);
  
  // Firma Bilgileri Modal state'i
  const [isCompanyDetailsModalOpen, setIsCompanyDetailsModalOpen] = useState(false);
  
  // Kullanıcı Sorgulama Modal state'leri
  const [isUserQueryModalOpen, setIsUserQueryModalOpen] = useState(false);
  
  // Fatura Detayı Modal state'leri
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEttn, setSelectedEttn] = useState<string | null>(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string | null>(null);
  
  // PavoPay fatura görüntüleme state'leri
  const [pavoPayViewerOpen, setPavoPayViewerOpen] = useState(false);
  const [pavoPayInvoiceHtml, setPavoPayInvoiceHtml] = useState<string>('');
  const [pavoPayViewerLoading, setPavoPayViewerLoading] = useState(false);
  const [pavoPayViewerInvoiceRef, setPavoPayViewerInvoiceRef] = useState<string>('');

  // Download menu state'leri
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloadMenuEttn, setDownloadMenuEttn] = useState<string | null>(null);
  // İndirilecek belgenin yönü (EDM'de gelen/giden farklı DIRECTION ile aranır)
  const [downloadMenuInvoiceType, setDownloadMenuInvoiceType] = useState<'GELEN' | 'GIDEN'>('GELEN');
  const downloadMenuOpen = Boolean(downloadMenuAnchor);
  
  // Tab state'i
  const [currentTab, setCurrentTab] = useState(0); // 0: Gelen E-Faturalar, 1: Giden E-Faturalar
  
  // Giden e-faturalar için state'ler
  const [gidenEInvoices, setGidenEInvoices] = useState<GidenEInvoiceData[]>([]);
  const [filteredGidenEInvoices, setFilteredGidenEInvoices] = useState<GidenEInvoiceData[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Gelen kutusu: yalnızca okunmamış faturaları getir (EDM readIncluded=false)
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  // Okundu/okunmadı işaretleme işlemi sürüyor mu (aksiyon menüsü için)
  const [markingRead, setMarkingRead] = useState<boolean>(false);
  
  // Snackbar state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  
  // ✅ YENİ: Durum bilgileri için state (ETTN -> Durum mapping)
  const [invoiceStatusMap, setInvoiceStatusMap] = useState<{ [ettn: string]: any }>({});

  // ✅ EDM: Bu oturumda gönderilen kabul/red yanıtları (1=Red, 2=Kabul).
  // GİB durumu işleyene kadar liste yenilense bile yerel olarak korunur.
  const [respondedOverrides, setRespondedOverrides] = useState<{ [ettn: string]: number }>({});
  
  // ✅ YENİ: İçeri aktarılan e-faturaların ETTN'leri (GELEN faturalar için)
  const [importedInvoiceETTNs, setImportedInvoiceETTNs] = useState<Set<string>>(new Set());
  
  // Üst menü için 3 nokta menüsü state'leri
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  
  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };
  
  // İşlemler sütunu 3 nokta menüsü state'leri
  const [anchorElActions, setAnchorElActions] = useState<null | HTMLElement>(null);
  const [selectedActionEttn, setSelectedActionEttn] = useState<string | null>(null);
  const [selectedActionBelgeNo, setSelectedActionBelgeNo] = useState<string | null>(null);
  const [selectedActionInvoiceType, setSelectedActionInvoiceType] = useState<'GELEN' | 'GIDEN'>('GELEN');
  const [selectedActionKaynak, setSelectedActionKaynak] = useState<string>('');
  const [selectedActionBelgeOid, setSelectedActionBelgeOid] = useState<string>('');
  
  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, ettn: string, belgeNo: string, invoiceType: 'GELEN' | 'GIDEN', kaynak?: string, belgeOid?: string) => {
    setAnchorElActions(event.currentTarget);
    setSelectedActionEttn(ettn);
    setSelectedActionBelgeNo(belgeNo);
    setSelectedActionInvoiceType(invoiceType);
    setSelectedActionKaynak(kaynak || '');
    setSelectedActionBelgeOid(belgeOid || '');
  };
  
  const handleCloseActionsMenu = () => {
    setAnchorElActions(null);
    setSelectedActionEttn(null);
    setSelectedActionBelgeNo(null);
    setSelectedActionInvoiceType('GELEN');
    setSelectedActionKaynak('');
    setSelectedActionBelgeOid('');
  };

  // EDM kalan kontörü sorgular (CheckCounter). Kontör kavramı EDM'e özgüdür.
  const loadKontor = async () => {
    setKontorLoading(true);
    setKontorError(null);
    try {
      const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;
      const response = await einvoiceApi.post(EINVOICE_API.EDM_KONTOR_SORGULA, { branchId });
      const result = response.data;
      if (result?.success && result.kalanKontor !== undefined && result.kalanKontor !== null) {
        const parsed = Number(result.kalanKontor);
        setKontorLeft(Number.isFinite(parsed) ? parsed : null);
      } else {
        setKontorLeft(null);
        setKontorError(result?.error || tt('Kontör bilgisi alınamadı', 'Could not fetch credits'));
      }
    } catch (err: any) {
      // Kontör bilgisi kritik değil; göstergede hata durumu gösterilir
      console.error('❌ [E-INVOICE] Kontör sorgulanamadı:', err);
      const data = err?.response?.data;
      setKontorLeft(null);
      setKontorError(data?.error || tt('Kontör sorgulanamadı (e-fatura servisi erişilemiyor olabilir)', 'Credit query failed (e-invoice service may be unreachable)'));
    } finally {
      setKontorLoading(false);
    }
  };

  // Aktif entegratör EDM ise kontörü çek (şube filtresi değişince de yenile).
  // Provider gelen kutusu akışında henüz çözülmediyse environment ucundan tespit edilir.
  useEffect(() => {
    let cancelled = false;
    const resolveAndLoad = async () => {
      if (eInvoiceProvider === 'edm') {
        setKontorProviderIsEdm(true);
        loadKontor();
        return;
      }
      if (eInvoiceProvider !== null) {
        // EDM dışı entegratör kesinleşti → gösterge gizli
        setKontorProviderIsEdm(false);
        setKontorLeft(null);
        setKontorError(null);
        return;
      }
      // Provider henüz bilinmiyor: einvoice API'den entegratörü sor
      try {
        const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;
        const url = branchId
          ? `${EINVOICE_API.EDM_ENVIRONMENT}?branchId=${encodeURIComponent(branchId)}`
          : EINVOICE_API.EDM_ENVIRONMENT;
        const resp = await einvoiceApi.get(url);
        if (!cancelled && resp.data?.success && String(resp.data.provider || '').toLowerCase() === 'edm') {
          setKontorProviderIsEdm(true);
          loadKontor();
        }
      } catch {
        // EDM entegrasyonu yok veya servis erişilemiyor → gösterge gizli kalır
      }
    };
    resolveAndLoad();
    return () => { cancelled = true; };
  }, [eInvoiceProvider, branchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gelen kutusundaki faturayı OKUNDU/OKUNMADI olarak işaretle (EDM MarkInvoice)
  const handleMarkInboxRead = async (ettn: string, belgeNo: string | null, read: boolean) => {
    if (eInvoiceProvider !== 'edm') return;
    setMarkingRead(true);
    try {
      const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;
      const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_MARK, {
        uuid: ettn || null,
        id: belgeNo || null,
        read,
        branchId,
      });
      const data = response.data;
      if (!data?.success) {
        setSnackbarMessage(data?.error || tt('İşaretleme başarısız oldu.', 'Failed to update read status.'));
        setSnackbarOpen(true);
        return;
      }

      // Yerel durumu güncelle: liste yeniden çekilmeden okundu/okunmadı yansısın
      if (ettn) {
        setInvoiceStatusMap((prev) => ({
          ...prev,
          [ettn]: { ...(prev[ettn] || {}), isRead: read },
        }));
      }

      // Yalnızca okunmamış filtresi açıkken okundu işaretlenen kayıt listeden düşmeli
      if (unreadOnly && read) {
        setGelenEInvoices((prev) => prev.filter((inv) => (inv.ettn || inv.belgeNo) !== (ettn || belgeNo)));
      }

      setSnackbarMessage(
        read
          ? tt('Fatura okundu olarak işaretlendi.', 'Invoice marked as read.')
          : tt('Fatura okunmadı olarak işaretlendi.', 'Invoice marked as unread.'),
      );
      setSnackbarOpen(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.errorLong ||
        err?.response?.data?.error ||
        err?.message ||
        tt('İşaretleme sırasında hata oluştu.', 'Error while updating read status.');
      setSnackbarMessage(msg);
      setSnackbarOpen(true);
    } finally {
      setMarkingRead(false);
    }
  };
  
  // Durum Sorgula Modal state'leri
  const [isStatusQueryModalOpen, setIsStatusQueryModalOpen] = useState(false);
  const [statusQueryEttn, setStatusQueryEttn] = useState<string | null>(null);
  const [statusQueryBelgeNo, setStatusQueryBelgeNo] = useState<string | null>(null);
  const [statusQueryInvoiceType, setStatusQueryInvoiceType] = useState<'GELEN' | 'GIDEN'>('GELEN'); // ✅ Fatura yönü
  
  const handleOpenStatusQueryModal = (ettn: string, belgeNo: string, invoiceType: 'GELEN' | 'GIDEN') => {
    setStatusQueryEttn(ettn);
    setStatusQueryBelgeNo(belgeNo);
    setStatusQueryInvoiceType(invoiceType); // ✅ Fatura yönünü kaydet
    setIsStatusQueryModalOpen(true);
  };
  
  const handleCloseStatusQueryModal = () => {
    setIsStatusQueryModalOpen(false);
    setStatusQueryEttn(null);
    setStatusQueryBelgeNo(null);
    setStatusQueryInvoiceType('GELEN');
  };
  
  // Tarihçe Modal state'leri
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyEttn, setHistoryEttn] = useState<string | null>(null);
  const [historyInvoiceType, setHistoryInvoiceType] = useState<'GELEN' | 'GIDEN'>('GELEN'); // ✅ Fatura yönü
  
  const handleOpenHistoryModal = (ettn: string, invoiceType: 'GELEN' | 'GIDEN') => {
    setHistoryEttn(ettn);
    setHistoryInvoiceType(invoiceType); // ✅ Fatura yönünü kaydet
    setIsHistoryModalOpen(true);
  };
  
  // Response Modal (Kabul/Red) state'leri
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [responseEttn, setResponseEttn] = useState<string | null>(null);
  const [responseBelgeNo, setResponseBelgeNo] = useState<string | null>(null);
  const [responseType, setResponseType] = useState<'KABUL' | 'RED'>('KABUL');
  
  const handleOpenResponseModal = (ettn: string, belgeNo: string, type: 'KABUL' | 'RED') => {
    setResponseEttn(ettn);
    setResponseBelgeNo(belgeNo);
    setResponseType(type);
    setIsResponseModalOpen(true);
    handleCloseActionsMenu(); // İşlemler menüsünü kapat
  };
  
  const handleCloseResponseModal = async () => {
    setIsResponseModalOpen(false);
    setResponseEttn(null);
    setResponseBelgeNo(null);
    setResponseType('KABUL');
    
    // EDM'de QNB durum sorgusu çalıştırma (uygulanamaz); durum onSuccess ile zaten
    // iyimser güncellendi, tam senkron için kullanıcı listeyi yenileyebilir.
    if (eInvoiceProvider === 'edm') return;

    // Modal kapatıldıktan sonra durum sorgulama yap (yanıt gönderildiyse durumu güncellenecek)
    console.log('🔄 [RESPONSE MODAL CLOSED] Durum sorgulaması tetikleniyor...');
    if (currentTabData.length > 0) {
      const invoiceType = currentTab === 0 ? 'GELEN' : 'GIDEN';
      await queryMultipleInvoiceStatus(currentTabData, invoiceType);
    }
  };
  
  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setHistoryEttn(null);
    setHistoryInvoiceType('GELEN');
  };
  
  // Yeni E-Fatura menüsü için state'ler
  const [anchorElNewInvoice, setAnchorElNewInvoice] = useState<null | HTMLElement>(null);
  
  const handleOpenNewInvoiceMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNewInvoice(event.currentTarget);
  };
  
  const handleCloseNewInvoiceMenu = () => {
    setAnchorElNewInvoice(null);
  };
  
  // Gelen E-Faturalar için tablo sütunları - useMemo ile optimize et
  const gelenTableColumns: TableColumn[] = useMemo(() => [
    { id: 'rowNumber', label: tt('Sıra', 'No'), field: 'rowNumber', visible: true, fixed: true }, // ✅ YENİ: Sıra sütunu
    { id: 'belgeNo', label: tt('Belge No', 'Document No'), field: 'belgeNo', visible: true, fixed: true }, // ✅ Resmî belge no öne alındı
    { id: 'ettn', label: 'ETTN', field: 'ettn', visible: true, fixed: false }, // Belge no'nun yanında; istenirse kolon seçiciden gizlenebilir
    { id: 'saticiUnvan', label: tt('Satıcı Ünvan', 'Seller Title'), field: 'saticiUnvan', visible: true, fixed: false },
    { id: 'belgeTarihi', label: tt('Belge Tarihi', 'Document Date'), field: 'belgeTarihi', visible: true, fixed: false },
    { id: 'belgeTuru', label: tt('Belge Türü', 'Document Type'), field: 'belgeTuru', visible: true, fixed: false },
    { id: 'faturaProfili', label: tt('Fatura Profili', 'Invoice Profile'), field: 'faturaProfili', visible: true, fixed: false }, // ✅ YENİ: Fatura Profili (Temel/Ticari)
    { id: 'gonderenVknTckn', label: tt('Gönderen VKN', 'Sender Tax ID'), field: 'gonderenVknTckn', visible: true, fixed: false },
    { id: 'toplamTutar', label: tt('Toplam Tutar', 'Total Amount'), field: 'toplamTutar', visible: true, fixed: false }, // ✅ YENİ: Toplam Tutar sütunu
    { id: 'durum', label: tt('Durum', 'Status'), field: 'durum', visible: true, fixed: false }, // ✅ YENİ: Durum sütunu
    { id: 'okunma', label: tt('Okunma', 'Read'), field: 'okunma', visible: true, fixed: false }, // ✅ YENİ: Okundu/Okunmadı göstergesi
    { id: 'imported', label: tt('İçeri Aktarıldı', 'Imported'), field: 'imported', visible: true, fixed: false }, // ✅ YENİ: İçeri aktarıldı mı?
    { id: 'actions', label: tt('İşlemler', 'Actions'), field: 'actions', visible: true, fixed: true },
  ], [isTr]);

  // Giden E-Faturalar için tablo sütunları - useMemo ile optimize et
  const gidenTableColumns: TableColumn[] = useMemo(() => [
    { id: 'yerelBelgeNo', label: tt('Belge No', 'Document No'), field: 'yerelBelgeNo', visible: true, fixed: true }, // ✅ Resmî belge no öne alındı
    { id: 'ettn', label: 'ETTN', field: 'ettn', visible: true, fixed: false }, // Belge no'nun yanında; istenirse kolon seçiciden gizlenebilir
    { id: 'belgeOid', label: tt('Belge OID', 'Document OID'), field: 'belgeOid', visible: false, fixed: false }, // ✅ Gizlendi
    { id: 'aliciUnvan', label: tt('Alıcı Ünvan', 'Receiver Title'), field: 'aliciUnvan', visible: true, fixed: false }, // ✅ YENİ: Alıcı ticari ünvanı
    { id: 'alimZamani', label: tt('Belge Tarihi', 'Document Date'), field: 'alimZamani', visible: true, fixed: false },
    { id: 'gidenTutar', label: tt('Toplam Tutar', 'Total Amount'), field: 'odenecekTutar', visible: true, fixed: false }, // ✅ YENİ: Ödenecek tutar
    { id: 'gidenProfil', label: tt('Fatura Profili', 'Invoice Profile'), field: 'profileId', visible: true, fixed: false }, // ✅ YENİ: Temel/Ticari/e-Arşiv
    { id: 'alimDurumu', label: tt('İşlem Durumu', 'Processing Status'), field: 'alimDurumu', visible: true, fixed: false },
    { id: 'kullaniciKodu', label: 'VKN', field: 'kullaniciKodu', visible: false, fixed: false }, // ✅ Gizlendi (gönderen kendi VKN'miz)
    { id: 'kaynak', label: tt('Kaynak', 'Source'), field: 'kaynak', visible: false, fixed: false }, // ✅ Gizlendi
    { id: 'hataMesaji', label: tt('Hata Mesajı', 'Error Message'), field: 'hataMesaji', visible: false, fixed: false }, // ✅ Gizlendi
    { id: 'durum', label: tt('Durum', 'Status'), field: 'durum', visible: true, fixed: false }, // ✅ YENİ: Durum sütunu
    { id: 'actions', label: tt('İşlemler', 'Actions'), field: 'actions', visible: true, fixed: true },
  ], [isTr]);

  // Tab'a göre aktif tablo sütunları
  const tableColumns = useMemo(() => {
    return currentTab === 0 ? gelenTableColumns : gidenTableColumns;
  }, [currentTab]);
  
  // Varsayılan tarih aralığı: son 1 gün (dünden bugüne)
  useEffect(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };
    
    setStartDate(formatDate(yesterday));
    setEndDate(formatDate(today));
  }, []);

  // Şirket ID'sini yükleme
  useEffect(() => {
    const loadCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError('Kullanıcı oturumu açık değil.');
        return;
      }

      try {
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData) {
          const currentCompanyId = userData.companyId;
          setCompanyId(currentCompanyId || null);
          
          // ✅ YENİ: İlk yüklemede içeri aktarılan faturaları kontrol et
          if (currentCompanyId) {
            checkImportedInvoices(currentCompanyId);
          }
        } else {
          setError('Kullanıcı bilgileri bulunamadı.');
        }
      } catch (err) {
        console.error('Şirket ID yüklenirken hata:', err);
        setError('Şirket bilgileri yüklenirken bir hata oluştu.');
      }
    };
    
    loadCompanyId();
  }, []);

  // Subeleri Firebase'den yukle
  useEffect(() => {
    if (!companyId) return;
    const loadBranches = async () => {
      try {
        const serviceBranches = await getBranchesAdapter(companyId);
        const map: Record<string, string> = {};
        serviceBranches.forEach((b) => {
          map[b.id] = b.name || tt('İsimsiz Şube', 'Unnamed Branch');
        });
        setBranchMap(map);
      } catch (err) {
        console.error('Şubeler yüklenemedi:', err);
      }
    };
    loadBranches();
  }, [companyId]);

  // PavoPay entegrasyonlarindan sube-BranchId eslestirme haritasi olustur
  useEffect(() => {
    if (!companyId) return;
    const loadPavoPayMap = async () => {
      try {
        const response = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
        const list = response.data?.data || response.data?.Data || [];
        console.log('🔍 [PAVOPAY MAP] API response list:', JSON.stringify(list.map((i: any) => ({ id: i.id ?? i.Id, branchId: i.branchId ?? i.BranchId, branchName: i.branchName ?? i.BranchName }))));
        const map: Record<string, string> = {};
        let foundGlobal = false;
        list.forEach((item: any) => {
          const bId = item.branchId ?? item.BranchId;
          if (!bId) {
            foundGlobal = true;
            return;
          }
          map[bId] = bId;
        });
        console.log('🔍 [PAVOPAY MAP] result:', { foundGlobal, map });
        setPavoPayBranchMap(map);
        setHasGlobalPavoPayIntegration(foundGlobal);
      } catch (err) {
        console.error('PavoPay entegrasyonları yüklenemedi:', err);
      }
    };
    loadPavoPayMap();
  }, [companyId, branchMap]);

  // Manager sube degistiginde filtreyi guncelle
  useEffect(() => {
    if (isManager && managerBranchId) {
      setBranchFilter(managerBranchId);
    }
  }, [isManager, managerBranchId]);

  // Secili subenin PavoPay BranchId'sini hesapla
  // E-Invoice'da sube bazli PavoPay sadece manager kullanabilir; admin sadece global (subesiz) entegrasyonu kullanir
  const activePavoPayBranchId = useMemo(() => {
    if (branchFilter === 'all') return undefined;
    if (!isManager) return undefined;
    return pavoPayBranchMap[branchFilter] || undefined;
  }, [branchFilter, pavoPayBranchMap, isManager]);

  // ✅ YENİ: İçeri aktarılan e-faturaları kontrol et (Alış faturalarından ETTN'leri çek)
  const checkImportedInvoices = async (currentCompanyId: string) => {
    try {
      console.log('📥 [İÇERI AKTARIM] Alış faturalarından ETTN kontrol ediliyor...', { companyId: currentCompanyId });
      
      // Tüm alış faturalarını çek
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_COMPANY());
      const result = response.data;
      const invoices = result.data || [];
      
      // ✅ DEBUG: İlk 3 alış faturasını göster
      console.log('🔍 [DEBUG] Backend\'den gelen İLK 3 fatura (ALIŞLAR):', 
        invoices.filter((inv: any) => inv.invoiceType === 'PURCHASE').slice(0, 3)
      );
      
      // Sadece alış faturalarını filtrele ve ETTN'leri topla
      const purchaseInvoiceETTNs: string[] = invoices
        .filter((inv: any) => 
          (inv.invoiceType === 'PURCHASE' || inv.type === 'purchase') && 
          (inv.eInvoiceETTN || inv.EInvoiceETTN)
        )
        .map((inv: any) => (inv.eInvoiceETTN || inv.EInvoiceETTN) as string)
        .filter((ettn: any): ettn is string => Boolean(ettn));
      
      const ettnSet = new Set<string>(purchaseInvoiceETTNs);
      setImportedInvoiceETTNs(ettnSet);
      
      console.log(`✅ [İÇERI AKTARIM] ${ettnSet.size} adet e-fatura içeri aktarılmış`, Array.from(ettnSet));
    } catch (error) {
      console.error('❌ [İÇERI AKTARIM] Kontrol hatası:', error);
    }
  };

  // E-fatura verilerini yükleme (POST method ile)
  const loadEInvoices = async (unreadOverride?: boolean) => {
    if (!companyId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📥 [E-INVOICE] SQL\'den entegrasyon bilgileri yükleniyor...', { companyId });

      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        // Finansbank/EDM yok — PavoPay dene
        await loadGelenViaPavoPay();
        setLoading(false);
        return;
      }

      console.log('✅ [E-INVOICE] Entegrasyon bilgileri SQL\'den yüklendi');

      // Aktif entegratör tespiti
      const provider = (sqlData.providerType || sqlData.ProviderType || '').toString().toLowerCase();
      setEInvoiceProvider(provider || null);

      // ✅ EDM entegrasyonu → gelen kutusunu EDM SOAP üzerinden çek
      if (provider === 'edm') {
        await loadGelenViaEdm(unreadOverride);
        setLoading(false);
        return;
      }

      // API URL'ini .env'den al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        setError('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        setLoading(false);
        return;
      }

      // API endpoint'ini oluştur (POST method ile query parameters)
      const endpoint = `${apiUrl}/QNBESolutions/GelenBelgeleriAlExt`;
      
      // Query parameters'ı hazırla (tarih parametreleri eklendi)
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password, // Plain text (backend'de decrypt edilmiş)
        vkn: sqlData.taxNumber,
        baslangic: startDate, // yyyyMMdd formatında
        bitis: endDate, // yyyyMMdd formatında
        belgeTuru: 'FATURA' // Sabit belge türü
      });

      const fullUrl = `${endpoint}?${params.toString()}`;
      console.log('Gelen e-fatura API çağrısı yapılıyor (POST with query):', { 
        endpoint, 
        query: { 
          username: sqlData.username, 
          password: '***', // Maskeleme
          vkn: sqlData.taxNumber,
          baslangic: startDate,
          bitis: endDate,
          belgeTuru: 'FATURA'
        },
        fullUrl: fullUrl
      });

      // API çağrısı yap (POST method ile ama query parameters)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      console.log('API response status (POST with query):', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response (POST with query):', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data: GelenEInvoiceApiResponse = await response.json();
      
      // Gelen verileri state'e kaydet - her zaman array olduğundan emin ol
      const belgeler = Array.isArray(data.belgeler) ? data.belgeler : [];
      
      // ✅ DEBUG: Gelen faturaların JSON yapısını kontrol et
      console.log('📥 [DEBUG belgev6] Gelen E-Fatura API Yanıtı:', data);
      console.log('📥 [DEBUG belgev6] İlk fatura örneği:', belgeler[0]);
      if (belgeler[0]) {
        console.log('📥 [DEBUG belgev6] Toplam Tutar:', belgeler[0].odenecekTutar);
        console.log('📥 [DEBUG belgev6] Para Birimi:', belgeler[0].odenecekTutarDovizCinsi);
        console.log('📥 [DEBUG belgev6] Profile ID:', belgeler[0].profileId);
        console.log('📥 [DEBUG belgev6] Tüm alanlar:', Object.keys(belgeler[0]));
      }
      
      setGelenEInvoices(belgeler);
      console.log(`${data.toplamBelge || 0} adet gelen e-fatura yüklendi (POST with query)`);

      // Snackbar bildirimi göster
      if (belgeler.length === 0) {
        const startDateFormatted = `${startDate.substring(6,8)}.${startDate.substring(4,6)}.${startDate.substring(0,4)}`;
        const endDateFormatted = `${endDate.substring(6,8)}.${endDate.substring(4,6)}.${endDate.substring(0,4)}`;
        setSnackbarMessage(`${startDateFormatted} - ${endDateFormatted} tarihleri arasında gelen e-fatura bulunamadı.`);
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(`${belgeler.length} adet gelen e-fatura bulundu.`);
        setSnackbarOpen(true);
        
        // ✅ GELEN faturalar için TEK TEK hızlı sorgulama
        queryMultipleInvoiceStatusOneByOne(belgeler, 'GELEN');
        
        // ✅ YENİ: İçeri aktarılan faturaları kontrol et
        if (companyId) {
          checkImportedInvoices(companyId);
        }
      }

    } catch (err: any) {
      // 404 — Finansbank/EDM entegrasyonu bulunamadı → PavoPay fallback
      if (err?.response?.status === 404 || (err instanceof Error && err.message.includes('404'))) {
        console.log('📥 [E-INVOICE GELEN] Finansbank/EDM entegrasyonu yok, PavoPay fallback deneniyor...');
        try {
          await loadGelenViaPavoPay();
        } catch (fallbackErr) {
          console.error('[E-INVOICE GELEN] PavoPay fallback de başarısız:', fallbackErr);
          setError('Ne Finansbank/EDM ne de PavoPay entegrasyonu üzerinden veri alınamadı.');
        }
        setLoading(false);
        return;
      }
      
      console.error('Gelen e-fatura verileri yüklenirken hata (POST with query):', err);

      // 500 Internal Server Error - genellikle fatura bulunamadığında oluşur
      if (err instanceof Error && err.message.includes('500') && err.message.includes('Internal Server Error')) {
        setGelenEInvoices([]);
        setError(null);
        console.log('Seçilen tarih aralığında gelen e-fatura bulunamadı.');
      }
      // CORS hatası için özel mesaj
      else if (err instanceof Error && err.message.includes('CORS')) {
        setError('CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor. Lütfen sistem yöneticisine başvurun veya API sunucusunda CORS ayarlarını kontrol edin.');
      } 
      // Bağlantı hatası
      else if (err instanceof Error && err.message.includes('Failed to fetch')) {
        setError('Bağlantı hatası: API sunucusuna POST isteği (query parameters ile) gönderilemedi. Lütfen API sunucusunun çalıştığından ve ağ bağlantınızın aktif olduğundan emin olun.');
      } 
      // Diğer hatalar
      else {
        setError(err instanceof Error ? err.message : 'Gelen e-fatura verileri yüklenirken bir hata oluştu (POST with query).');
      }
    } finally {
      setLoading(false);
    }
  };

  // EDM entegratörü üzerinden gelen e-faturaları çek (SOAP: GetInvoice DIRECTION=IN)
  const loadGelenViaEdm = async (unreadOverride?: boolean): Promise<boolean> => {
    console.log('📥 [E-INVOICE GELEN] EDM üzerinden gelen kutusu sorgulanıyor...');
    try {
      // startDate/endDate "yyyyMMdd" formatında → ISO'ya çevir
      const toIso = (yyyymmdd: string, endOfDay: boolean) => {
        if (!yyyymmdd || yyyymmdd.length < 8) return new Date().toISOString();
        const y = yyyymmdd.substring(0, 4);
        const m = yyyymmdd.substring(4, 6);
        const d = yyyymmdd.substring(6, 8);
        return new Date(`${y}-${m}-${d}T${endOfDay ? '23:59:59' : '00:00:00'}`).toISOString();
      };

      const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;

      const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX, {
        startDate: toIso(startDate, false),
        endDate: toIso(endDate, true),
        // unreadOnly=true → yalnızca okunmamışlar (readIncluded=false).
        // override verilirse (toggle anındaki stale state'i önlemek için) onu kullan.
        readIncluded: !(unreadOverride !== undefined ? unreadOverride : unreadOnly),
        limit: 200,
        branchId,
      });

      const data = response.data;
      if (!data?.success) {
        setError(data?.error || tt('Gelen kutusu (EDM) alınamadı.', 'Failed to load inbox (EDM).'));
        setGelenEInvoices([]);
        return false;
      }

      const items: any[] = Array.isArray(data.items) ? data.items : [];

      const formatDateTr = (iso?: string) => {
        if (!iso) return '';
        const dt = new Date(iso);
        if (isNaN(dt.getTime())) return iso;
        const dd = `${dt.getDate()}`.padStart(2, '0');
        const mm = `${dt.getMonth() + 1}`.padStart(2, '0');
        return `${dd}.${mm}.${dt.getFullYear()}`;
      };

      const mapped: GelenEInvoiceData[] = items.map((it) => ({
        belgeNo: it.id || '',
        belgeSiraNo: '',
        belgeTarihi: formatDateTr(it.issueDate),
        belgeTuru: it.invoiceType || 'FATURA',
        ettn: it.uuid || it.id || '',
        gonderenEtiket: it.senderVkn || '',
        gonderenVknTckn: it.senderVkn || '',
        aliciUnvan: '',
        saticiUnvan: it.senderTitle || '',
        belgeVersiyon: '',
        zarfId: '',
        ekBilgiler: {},
        odenecekTutar: it.payableAmount != null ? String(it.payableAmount) : undefined,
        odenecekTutarDovizCinsi: it.currency || 'TRY',
        arsivlenmis: undefined,
        faturaGelisTarihi: formatDateTr(it.issueDate),
        profileId: it.profileId,
      }));

      setGelenEInvoices(mapped);

      // EDM başlık verisinden durum/profil haritasını doldur (QNB durum sorgusuna gerek yok).
      // GİB profili: TICARIFATURA → yanıt verilebilir; TEMELFATURA/diğer → yanıt gerekmez.
      // Ticari faturada gerçek yanıt durumu (Kabul/Red), EDM başlığındaki yanıt/durum
      // açıklamalarından türetilir; yoksa "Yanıt Bekleniyor".
      const statusMap: { [ettn: string]: any } = {};
      items.forEach((it) => {
        const key = it.uuid || it.id || '';
        if (!key) return;
        const profile = (it.profileId || '').toString().toUpperCase();
        const isTicari = profile === 'TICARIFATURA';

        // Yanıt/durum açıklamalarını tek metinde birleştirip Kabul/Red ara
        const blob = [it.responseDescription, it.responseCode, it.gibStatusDescription, it.statusDescription, it.status]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();
        const isRejected = blob.includes('REDDEDİL') || blob.includes('REDDEDIL') || blob.includes('REJECT');
        const isAccepted = blob.includes('KABUL') || blob.includes('ACCEPT');

        let yanitDurumu: number;
        if (!isTicari) yanitDurumu = -1;          // Temel fatura → yanıt gerekmez
        else if (isRejected) yanitDurumu = 1;      // Reddedildi
        else if (isAccepted) yanitDurumu = 2;      // Kabul edildi
        else yanitDurumu = 0;                      // Yanıt bekleniyor

        // EDM/GİB henüz işlememişse, bu oturumda gönderdiğimiz yanıtı koru
        if (isTicari && yanitDurumu === 0 && respondedOverrides[key] != null) {
          yanitDurumu = respondedOverrides[key];
        }

        statusMap[key] = {
          yanitDurumu,
          profileId: it.profileId,
          durumAciklama: it.responseDescription || it.gibStatusDescription || it.status || '',
          isRead: !!it.isRead,
          edm: true,
        };
      });
      setInvoiceStatusMap((prev) => ({ ...prev, ...statusMap }));

      console.log(`✅ [E-INVOICE GELEN] EDM: ${mapped.length} adet gelen e-fatura yüklendi`);

      if (mapped.length === 0) {
        setSnackbarMessage(tt('Seçilen tarih aralığında gelen e-fatura bulunamadı.', 'No incoming e-invoices found in the selected date range.'));
      } else {
        setSnackbarMessage(`${mapped.length} ${tt('adet gelen e-fatura bulundu.', 'incoming e-invoices found.')}`);
        if (companyId) checkImportedInvoices(companyId);
      }
      setSnackbarOpen(true);
      return true;
    } catch (err: any) {
      const msg =
        err?.response?.data?.errorLong ||
        err?.response?.data?.error ||
        err?.message ||
        tt('Gelen kutusu (EDM) yüklenirken hata oluştu.', 'Error while loading inbox (EDM).');
      console.error('[E-INVOICE GELEN] EDM hatası:', err);
      setError(msg);
      setGelenEInvoices([]);
      return false;
    }
  };

  // PavoPay (Aktif Bank) üzerinden gelen e-fatura sorgulama
  const loadGelenViaPavoPay = async (): Promise<boolean> => {
    console.log('🔍 [PAVOPAY CHECK - GELEN]', {
      activePavoPayBranchId,
      hasGlobalPavoPayIntegration,
      isManager,
      branchFilter,
      pavoPayBranchMap,
    });
    if (!activePavoPayBranchId && !hasGlobalPavoPayIntegration) {
      console.log('⛔ [PAVOPAY CHECK - GELEN] Şube seçimi gerekli veya global entegrasyon yok, uyarı gösteriliyor.');
      setPavoPayBranchWarningOpen(true);
      return false;
    }
    console.log('📥 [E-INVOICE GELEN] PavoPay/Aktif Bank üzerinden sorgulama...');
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 30);

      const qs = `sort=&pageSize=50&page=1&group=&filter=&startDate=${start.toISOString()}&endDate=${now.toISOString()}&filterStatus=1,2,3,4,5,6,7,8,9,12,17,23,24&taxId=&byIssueDate=true&isArchived=&profileType=&invoiceType=`;

      const proxyResponse = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
        Path: '/api/EInvoice/IncomingInvoice/SearchInvoices',
        QueryString: qs,
        Method: 'GET',
        BranchId: activePavoPayBranchId
      });

      const aktifData = proxyResponse.data;
      console.log('✅ [E-INVOICE GELEN] Aktif Bank yanıtı:', aktifData);

      const items = aktifData?.Data || aktifData?.data || [];
      console.log('📋 [E-INVOICE GELEN] Aktif Bank items:', items.length, 'adet, ilk kayıt:', items[0]);
      if (Array.isArray(items) && items.length > 0) {
        const mapped: GelenEInvoiceData[] = items.map((item: any) => ({
          belgeNo: item.InvoiceReference || item.InvoiceNumber || '',
          belgeSiraNo: String(item.IncomingInvoiceId || item.OutgoingInvoiceId || ''),
          belgeTarihi: item.IssueDate || item.CreateDate || '',
          belgeTuru: item.InvoiceTypeName || 'FATURA',
          ettn: item.UUID || item.Ettn || '',
          gonderenEtiket: item.SenderAlias || '',
          gonderenVknTckn: item.SenderTaxOrIdNo || '',
          alanEtiket: item.PostboxAlias || '',
          aliciUnvan: item.PartyName || '',
          saticiUnvan: item.SenderPartyName || '',
          belgeVersiyon: item.UblVersionCode || '',
          zarfId: item.EnvelopeReference || '',
          ekBilgiler: null,
          odenecekTutar: String(item.TotalPayableAmount || item.TotalTaxInclusiveAmount || '0'),
          odenecekTutarDovizCinsi: item.DocCurrencyCode || 'TRY',
        }));
        setGelenEInvoices(mapped);
        setError(null);
        setSnackbarMessage(`${mapped.length} adet gelen e-fatura bulundu (PavoPay).`);
        setSnackbarOpen(true);
      } else {
        setGelenEInvoices([]);
        setError(null);
        setSnackbarMessage('Aktif Bank üzerinde gelen e-fatura bulunamadı.');
        setSnackbarOpen(true);
      }
      return true;
    } catch (pavoErr: any) {
      console.error('[E-INVOICE GELEN] PavoPay proxy hatası:', pavoErr);
      console.error('[E-INVOICE GELEN] PavoPay proxy response data:', pavoErr?.response?.data);
      console.error('[E-INVOICE GELEN] PavoPay proxy response status:', pavoErr?.response?.status);
      if (pavoErr?.response?.status === 404) {
        setError('Ne QNB/Finansbank ne de PavoPay entegrasyonu bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
      } else {
        const detail = pavoErr?.response?.data?.error || pavoErr?.response?.data?.message || pavoErr?.message || 'Bilinmeyen hata';
        setError(`PavoPay/Aktif Bank sorgusu başarısız: ${detail}`);
      }
      return false;
    }
  };

  // PavoPay (Aktif Bank) üzerinden giden e-fatura sorgulama
  const loadGidenViaPavoPay = async (): Promise<boolean> => {
    console.log('🔍 [PAVOPAY CHECK - GIDEN]', {
      activePavoPayBranchId,
      hasGlobalPavoPayIntegration,
      isManager,
      branchFilter,
      pavoPayBranchMap,
    });
    if (!activePavoPayBranchId && !hasGlobalPavoPayIntegration) {
      console.log('⛔ [PAVOPAY CHECK - GIDEN] Şube seçimi gerekli veya global entegrasyon yok, uyarı gösteriliyor.');
      setPavoPayBranchWarningOpen(true);
      return false;
    }
    console.log('📥 [E-INVOICE GIDEN] PavoPay/Aktif Bank üzerinden sorgulama...');
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 1);

      const qs = [
        'sort=', 'pageSize=10', 'page=1', 'group=', 'filter=',
        `startDate=${start.toISOString()}`,
        `endDate=${now.toISOString()}`,
        'filterStatus=1,2,3,4,5,6,7,8,9,12,17,23,24',
        'taxId=', 'byIssueDate=true', 'isArchived=', 'profileType=', 'invoiceType='
      ].join('&');

      console.log('📤 [E-INVOICE GIDEN] PavoPay proxy isteği:', { path: '/api/EInvoice/OutgoingInvoice/SearchInvoices', qs });

      const proxyResponse = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
        Path: '/api/EInvoice/OutgoingInvoice/SearchInvoices',
        QueryString: qs,
        Method: 'GET',
        BranchId: activePavoPayBranchId
      });

      const aktifData = proxyResponse.data;
      console.log('✅ [E-INVOICE GIDEN] Aktif Bank yanıtı:', aktifData);

      const items = aktifData?.Data || aktifData?.data || [];
      console.log('📋 [E-INVOICE GIDEN] Aktif Bank items:', items.length, 'adet, ilk kayıt:', items[0]);
      if (Array.isArray(items) && items.length > 0) {
        const mapped: GidenEInvoiceData[] = items.map((item: any) => ({
          belgeOid: String(item.OutgoingInvoiceId || item.Id || item.id || ''),
          ettn: item.UUID || item.uuid || item.Ettn || null,
          yerelBelgeNo: item.InvoiceReference || item.InvoiceNumber || '',
          alimZamani: item.IssueDate || item.CreateDate || item.SendingDate || '',
          alimDurumu: item.ResponseDescription || item.GibResponseDsc || String(item.Status || ''),
          hataMesaji: item.ErrorMessage || null,
          islemYapanVkn: item.SenderTaxOrIdNo || null,
          kullaniciKodu: item.UserFullName || '',
          kaynak: 'PavoPay',
          aliciUnvan: item.ReceiverName || item.ReceiverTitle || item.CustomerName || '',
          odenecekTutar: item.PayableAmount != null
            ? String(item.PayableAmount)
            : (item.TotalAmount != null ? String(item.TotalAmount) : undefined),
          odenecekTutarDovizCinsi: item.CurrencyCode || item.Currency || 'TRY',
        }));
        setGidenEInvoices(mapped);
        setError(null);
        setSnackbarMessage(`${mapped.length} adet giden e-fatura bulundu (PavoPay).`);
        setSnackbarOpen(true);
      } else {
        setGidenEInvoices([]);
        setError(null);
        setSnackbarMessage('Aktif Bank üzerinde giden e-fatura bulunamadı.');
        setSnackbarOpen(true);
      }
      return true;
    } catch (pavoErr: any) {
      console.error('[E-INVOICE GIDEN] PavoPay proxy hatası:', pavoErr);
      console.error('[E-INVOICE GIDEN] PavoPay proxy response data:', pavoErr?.response?.data);
      console.error('[E-INVOICE GIDEN] PavoPay proxy response status:', pavoErr?.response?.status);
      if (pavoErr?.response?.status === 404) {
        setError('Ne QNB/Finansbank ne de PavoPay entegrasyonu bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
      } else {
        const detail = pavoErr?.response?.data?.error || pavoErr?.response?.data?.message || pavoErr?.message || 'Bilinmeyen hata';
        setError(`PavoPay/Aktif Bank sorgusu başarısız: ${detail}`);
      }
      return false;
    }
  };

  // EDM üzerinden giden e-faturaları sorgular (GetInvoice DIRECTION=OUT).
  // Credential backend'de SQL'den çözülür; frontend kullanıcı/şifre göndermez.
  const loadGidenViaEdm = async (): Promise<boolean> => {
    console.log('📤 [E-INVOICE GIDEN] EDM üzerinden giden kutusu sorgulanıyor...');
    try {
      const toIso = (yyyymmdd: string, endOfDay: boolean) => {
        if (!yyyymmdd || yyyymmdd.length < 8) return new Date().toISOString();
        const y = yyyymmdd.substring(0, 4);
        const m = yyyymmdd.substring(4, 6);
        const d = yyyymmdd.substring(6, 8);
        return new Date(`${y}-${m}-${d}T${endOfDay ? '23:59:59' : '00:00:00'}`).toISOString();
      };

      const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;

      const response = await einvoiceApi.post(EINVOICE_API.EDM_OUTBOX, {
        startDate: toIso(startDate, false),
        endDate: toIso(endDate, true),
        limit: 200,
        branchId,
      });

      const data = response.data;
      if (!data?.success) {
        setError(data?.error || tt('Giden kutusu (EDM) alınamadı.', 'Failed to load outbox (EDM).'));
        setGidenEInvoices([]);
        return false;
      }

      const items: any[] = Array.isArray(data.items) ? data.items : [];

      const formatDateTr = (iso?: string) => {
        if (!iso) return '';
        const dt = new Date(iso);
        if (isNaN(dt.getTime())) return iso || '';
        const dd = `${dt.getDate()}`.padStart(2, '0');
        const mm = `${dt.getMonth() + 1}`.padStart(2, '0');
        return `${dd}.${mm}.${dt.getFullYear()}`;
      };

      const mapped: GidenEInvoiceData[] = items.map((it) => {
        const statusBlob = [it.status, it.statusDescription, it.gibStatusDescription]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();
        const isFailed = /FAIL|ERROR|HATA/.test(statusBlob);
        return {
          belgeOid: '',
          ettn: it.uuid || null,
          yerelBelgeNo: it.id || '',
          alimZamani: formatDateTr(it.issueDate),
          alimDurumu: isFailed ? tt('İşleme Hatası', 'Processing Error') : tt('İşlendi', 'Processed'),
          hataMesaji: isFailed
            ? (it.gibStatusDescription || it.statusDescription || it.status || null)
            : null,
          islemYapanVkn: null,
          kullaniciKodu: it.senderVkn || '',
          kaynak: 'EDM',
          // ✅ EDM başlığından zengin alanlar: alıcı ünvanı, tutar, para birimi, profil
          aliciUnvan: it.receiverTitle || '',
          odenecekTutar: it.payableAmount != null ? String(it.payableAmount) : undefined,
          odenecekTutarDovizCinsi: it.currency || 'TRY',
          profileId: it.profileId,
        };
      });

      setGidenEInvoices(mapped);

      // Durum sütunu invoiceStatusMap'ten okunur; EDM başlık verisinden doldur
      // (QNB durum sorgusuna gerek yok — gelen kutusu akışıyla aynı mantık).
      const statusMap: { [ettn: string]: any } = {};
      items.forEach((it) => {
        const key = it.uuid || it.id || '';
        if (!key) return;
        const profile = (it.profileId || '').toString().toUpperCase();
        const isTicari = profile === 'TICARIFATURA';
        const blob = [it.responseDescription, it.responseCode, it.gibStatusDescription, it.statusDescription, it.status]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();
        const isRejected = blob.includes('REDDEDİL') || blob.includes('REDDEDIL') || blob.includes('REJECT');
        const isAccepted = blob.includes('KABUL') || blob.includes('ACCEPT');

        let yanitDurumu: number;
        if (!isTicari) yanitDurumu = -1;
        else if (isRejected) yanitDurumu = 1;
        else if (isAccepted) yanitDurumu = 2;
        else yanitDurumu = 0;

        statusMap[key] = { yanitDurumu, profileId: it.profileId };
      });
      setInvoiceStatusMap((prev) => ({ ...prev, ...statusMap }));

      setSnackbarMessage(
        mapped.length === 0
          ? tt('Seçilen tarih aralığında giden e-fatura bulunamadı (EDM).', 'No outgoing e-invoices found for the selected date range (EDM).')
          : tt(`${mapped.length} adet giden e-fatura bulundu (EDM).`, `${mapped.length} outgoing e-invoices found (EDM).`)
      );
      setSnackbarOpen(true);
      return true;
    } catch (err: any) {
      console.error('[E-INVOICE GIDEN] EDM giden kutusu hatası:', err);
      const data = err?.response?.data;
      setError(data?.errorShort || data?.error || tt('Giden kutusu (EDM) sorgulanamadı.', 'Failed to query outbox (EDM).'));
      setGidenEInvoices([]);
      return false;
    }
  };

  // Giden e-fatura verilerini yükleme (POST method ile)
  const loadGidenEInvoices = async () => {
    if (!companyId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📥 [E-INVOICE GIDEN] SQL\'den entegrasyon bilgileri yükleniyor...', { companyId });

      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        // Finansbank/EDM yok — PavoPay dene
        await loadGidenViaPavoPay();
        setLoading(false);
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      console.log('✅ [E-INVOICE GIDEN] Entegrasyon bilgileri SQL\'den yüklendi');

      // ✅ EDM entegrasyonu → giden kutusunu EDM SOAP üzerinden çek
      // (QNB endpoint'i EDM credential'larını tanımaz; 400 döner)
      const gidenProvider = (sqlData.providerType || sqlData.ProviderType || '').toString().toLowerCase();
      // ⚠️ Provider state'i burada da set edilmeli: kullanıcı doğrudan Giden sekmesinde
      // çalışırsa (Gelen hiç yüklenmeden) provider null kalıyor ve "Detayları Görüntüle"
      // yanlışlıkla QNB detay modalına düşüyordu.
      setEInvoiceProvider(gidenProvider || null);
      if (gidenProvider === 'edm') {
        await loadGidenViaEdm();
        setLoading(false);
        return;
      }
      if (gidenProvider === 'pavopay') {
        await loadGidenViaPavoPay();
        setLoading(false);
        return;
      }

      // API URL'ini .env'den al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        setError('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        setLoading(false);
        return;
      }

      // Giden belgeler API endpoint'ini oluştur
      const endpoint = `${apiUrl}/QNBESolutions/GidenBelgeleriListele`;
      
      // Query parameters'ı hazırla
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password,
        vkn: sqlData.taxNumber,
        baslangic: startDate,
        bitis: endDate,
        belgeTuru: 'FATURA_UBL'
      });

      const fullUrl = `${endpoint}?${params.toString()}`;

      // API çağrısı yap (POST method ile ama query parameters)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Giden e-fatura API error response:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data: GidenEInvoiceApiResponse = await response.json();
      
      const belgeler = Array.isArray(data.belgeler) ? data.belgeler : [];
      setGidenEInvoices(belgeler);

      if (belgeler.length === 0) {
        const startDateFormatted = `${startDate.substring(6,8)}.${startDate.substring(4,6)}.${startDate.substring(0,4)}`;
        const endDateFormatted = `${endDate.substring(6,8)}.${endDate.substring(4,6)}.${endDate.substring(0,4)}`;
        setSnackbarMessage(`${startDateFormatted} - ${endDateFormatted} tarihleri arasında giden e-fatura bulunamadı.`);
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(`${belgeler.length} adet giden e-fatura bulundu.`);
        setSnackbarOpen(true);
        queryMultipleInvoiceStatus(belgeler, 'GIDEN');
      }

    } catch (err: any) {
      // 404 — Finansbank/EDM entegrasyonu bulunamadı → PavoPay fallback
      if (err?.response?.status === 404 || (err instanceof Error && err.message.includes('404'))) {
        console.log('📥 [E-INVOICE GIDEN] Finansbank/EDM entegrasyonu yok, PavoPay fallback deneniyor...');
        try {
          await loadGidenViaPavoPay();
        } catch (fallbackErr) {
          console.error('[E-INVOICE GIDEN] PavoPay fallback de başarısız:', fallbackErr);
          setError('Ne Finansbank/EDM ne de PavoPay entegrasyonu üzerinden veri alınamadı.');
        }
        setLoading(false);
        return;
      }
      
      console.error('Giden e-fatura verileri yüklenirken hata:', err);

      // 500 Internal Server Error - genellikle fatura bulunamadığında oluşur
      if (err instanceof Error && err.message.includes('500') && err.message.includes('Internal Server Error')) {
        // Fatura bulunamadı durumu - hata gösterme, sadece boş liste bırak
        setGidenEInvoices([]);
        setError(null); // Hata mesajını temizle
        console.log('Seçilen tarih aralığında giden e-fatura bulunamadı.');
      }
      // CORS hatası için özel mesaj
      else if (err instanceof Error && err.message.includes('CORS')) {
        setError('CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor. Lütfen sistem yöneticisine başvurun veya API sunucusunda CORS ayarlarını kontrol edin.');
      } 
      // Bağlantı hatası
      else if (err instanceof Error && err.message.includes('Failed to fetch')) {
        setError('Bağlantı hatası: API sunucusuna POST isteği (query parameters ile) gönderilemedi. Lütfen API sunucusunun çalıştığından ve ağ bağlantınızın aktif olduğundan emin olun.');
      } 
      // Diğer hatalar
      else {
        setError(err instanceof Error ? err.message : 'Giden e-fatura verileri yüklenirken bir hata oluştu (POST with query).');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Açılışta ve sekme değişiminde otomatik yükleme.
  // Varsayılan tarih aralığı "son 1 gün" olduğu için ekran açılır açılmaz ve
  // Gelen/Giden sekmesine geçildiğinde güncel kayıtlar, Filtrele'ye basılmadan gelir.
  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;
    if (currentTab === 0) {
      loadEInvoices();
    } else {
      loadGidenEInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentTab]);

  // ✅ YENİ: Tek tek hızlı durum sorgulama (GELEN faturalar için)
  const queryMultipleInvoiceStatusOneByOne = async (invoices: any[], invoiceType: 'GELEN' | 'GIDEN') => {
    if (!companyId || invoices.length === 0) return;
    const filteredInvoices = invoices.filter((inv: any) => inv.kaynak !== 'PavoPay');
    if (filteredInvoices.length === 0) return;
    
    try {
      console.log(`🔍 [AUTO STATUS QUERY ONE-BY-ONE] ${invoiceType} faturalar için tek tek durum sorgulama başlıyor...`, { count: filteredInvoices.length });
      
      // ETTN'leri topla
      const ettnList = filteredInvoices.map(inv => inv.ettn).filter(Boolean);
      
      if (ettnList.length === 0) {
        console.log('⚠️ [AUTO STATUS QUERY ONE-BY-ONE] ETTN bulunamadı, durum sorgulaması atlanıyor');
        return;
      }
      
      // Entegrasyon bilgilerini al
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;
      
      if (!sqlData) {
        console.error('❌ [AUTO STATUS QUERY ONE-BY-ONE] Entegrasyon bilgileri bulunamadı');
        return;
      }
      
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        console.error('❌ [AUTO STATUS QUERY ONE-BY-ONE] API URL bulunamadı');
        return;
      }
      
      const endpoint = `${apiUrl}/QNBESolutions/GelenBelgeDurumSorgula`;
      
      console.log(`📤 [AUTO STATUS QUERY ONE-BY-ONE] ${ettnList.length} fatura için parallel sorgu başlatılıyor...`);
      
      // ✅ Her ETTN için parallel sorgu yap (Promise.allSettled ile hata toleransı)
      const queryPromises = ettnList.map(async (ettn) => {
        try {
          const requestBody = {
            username: sqlData.username,
            password: sqlData.password,
            vknTckn: sqlData.taxNumber,
            ettn: ettn,
            belgeTuru: 'FATURA'
          };
          
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
          
          const responseText = await response.text();
          
          if (!response.ok) {
            console.error(`❌ [AUTO STATUS QUERY ONE-BY-ONE] ${ettn} sorgulanamadı:`, responseText.substring(0, 100));
            return { ettn, error: true };
          }
          
          const statusResult = JSON.parse(responseText);
          
          if (statusResult.isSuccess !== false && statusResult.data) {
            const durum = statusResult.data;
            return {
              ettn,
              yanitDurumu: durum.YanitDurumu ?? durum.yanitDurumu,
              durumKodu: null, // GELEN'de yok
              yanitDetayi: durum.YanitDetayi || durum.yanitDetayi,
              durumAciklama: durum.YanitDurumuAciklama || durum.yanitDurumuAciklama,
            };
          }
          
          return { ettn, error: true };
        } catch (error) {
          console.error(`❌ [AUTO STATUS QUERY ONE-BY-ONE] ${ettn} sorgu hatası:`, error);
          return { ettn, error: true };
        }
      });
      
      // ✅ Tüm sorguların tamamlanmasını bekle
      const results = await Promise.allSettled(queryPromises);
      
      // ✅ Tüm sonuçları map'e kaydet (hem başarılı hem hatalı)
      const newStatusMap: { [ettn: string]: any } = {};
      let successCount = 0;
      let errorCount = 0;
      
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const data = result.value;
          if (data.error) {
            // Hata durumu
            newStatusMap[data.ettn] = {
              error: 'Sorgu hatası'
            };
            errorCount++;
          } else {
            // Başarılı durum
            newStatusMap[data.ettn] = {
              yanitDurumu: data.yanitDurumu,
              durumKodu: data.durumKodu,
              yanitDetayi: data.yanitDetayi,
              durumAciklama: data.durumAciklama,
            };
            successCount++;
          }
        } else {
          // Promise rejected
          errorCount++;
        }
      });
      
      setInvoiceStatusMap(prev => ({ ...prev, ...newStatusMap }));
      console.log(`✅ [AUTO STATUS QUERY ONE-BY-ONE] ${successCount}/${ettnList.length} faturanın durumu güncellendi${errorCount > 0 ? ` (${errorCount} hata)` : ''}`);
      
    } catch (error: any) {
      console.error('❌ [AUTO STATUS QUERY ONE-BY-ONE] Genel hata:', error);
    }
  };

  // ✅ YENİ: Çoklu fatura durum sorgulama (otomatik - GİDEN ve GELEN FATURALAR)
  const queryMultipleInvoiceStatus = async (invoices: any[], invoiceType: 'GELEN' | 'GIDEN') => {
    if (!companyId || invoices.length === 0) return;
    
    const nonPavoPayInvoices = invoices.filter((inv: any) => inv.kaynak !== 'PavoPay');
    if (nonPavoPayInvoices.length === 0) return;
    
    // GELEN faturalar için tek tek sorgu yap
    if (invoiceType === 'GELEN') {
      console.log('🔍 [AUTO STATUS QUERY - MODAL CLOSE] GELEN faturalar için tek tek sorgu başlatılıyor...', { count: nonPavoPayInvoices.length });
      await queryMultipleInvoiceStatusOneByOne(nonPavoPayInvoices, invoiceType);
      return;
    }
    
    try {
      console.log(`🔍 [AUTO STATUS QUERY] ${invoiceType} faturalar için otomatik durum sorgulama başlıyor...`, { count: nonPavoPayInvoices.length });
      
      // ETTN'leri topla
      const ettnList = nonPavoPayInvoices.map(inv => inv.ettn).filter(Boolean);
      
      if (ettnList.length === 0) {
        console.log('⚠️ [AUTO STATUS QUERY] ETTN bulunamadı, durum sorgulaması atlanıyor');
        return;
      }
      
      // Entegrasyon bilgilerini al
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;
      
      if (!sqlData) {
        console.error('❌ [AUTO STATUS QUERY] Entegrasyon bilgileri bulunamadı');
        return;
      }
      
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        console.error('❌ [AUTO STATUS QUERY] API URL bulunamadı');
        return;
      }
      
      // ✅ Sadece GİDEN faturalar için
      const endpoint = `${apiUrl}/QNBESolutions/BelgeDurumSorgula-multiple`;
      const requestBody = {
        username: sqlData.username,
        password: sqlData.password,
        vknTckn: sqlData.taxNumber,
        belgeNoList: ettnList, // ✅ Çoklu ETTN array
        belgeNoTipi: 'ETTN',
        belgeTuru: 'FATURA_UBL'
      };
      
      console.log(`📤 [AUTO STATUS QUERY] ${invoiceType} için toplu sorgu gönderiliyor...`, { count: ettnList.length });
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        console.error('❌ [AUTO STATUS QUERY] API hatası:', responseText);
        return;
      }
      
      let statusResult;
      try {
        statusResult = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ [AUTO STATUS QUERY] JSON parse hatası:', parseError);
        return;
      }
      
      console.log(`✅ [AUTO STATUS QUERY] ${invoiceType} durum bilgileri alındı:`, statusResult);
      
      // Durum bilgilerini map'e kaydet
      const newStatusMap: { [ettn: string]: any } = {};
      
      if (statusResult.isSuccess !== false && statusResult.data) {
        const dataArray = Array.isArray(statusResult.data) ? statusResult.data : [statusResult.data];
        
        dataArray.forEach((durum: any) => {
          const ettn = durum.Ettn || durum.ettn;
          if (ettn) {
            newStatusMap[ettn] = {
              yanitDurumu: durum.YanitDurumu ?? durum.yanitDurumu,
              durumKodu: durum.DurumKodu ?? durum.durumKodu,
              yanitDetayi: durum.YanitDetayi || durum.yanitDetayi,
              durumAciklama: durum.DurumAciklama || durum.durumAciklama || durum.YanitDurumuAciklama || durum.yanitDurumuAciklama,
            };
          }
        });
        
        setInvoiceStatusMap(prev => ({ ...prev, ...newStatusMap }));
        console.log(`✅ [AUTO STATUS QUERY] ${Object.keys(newStatusMap).length} faturanın durumu güncellendi`);
      }
    } catch (error: any) {
      console.error('❌ [AUTO STATUS QUERY] Hata:', error);
    }
  };

  // Otomatik yükleme işlemleri kaldırıldı - kullanıcı manuel filtrele butonuna basacak
  
  // Yeni E-Fatura oluşturma
  const handleNewSalesInvoice = () => {
    handleCloseNewInvoiceMenu();
    navigate('/fatura/satis/yeni');
  };

  const handleNewPurchaseInvoice = () => {
    handleCloseNewInvoiceMenu();
    navigate('/fatura/alis/yeni');
  };

  const handleImportEInvoice = (ettn: string) => {
    console.log('📧 [E-INVOICE] E-Fatura içe aktarılıyor:', ettn);
    // PurchaseInvoice sayfasına ETTN ile yönlendir
    navigate(`/fatura/alis/yeni?ettn=${ettn}`);
  };

  // Entegrasyon ayarları modalını açma
  const handleOpenIntegrationModal = () => {
    setIsIntegrationModalOpen(true);
  };

  // Entegrasyon ayarları modalını kapatma
  const handleCloseIntegrationModal = (updated?: boolean) => {
    setIsIntegrationModalOpen(false);
    if (updated) {
      console.log('Entegrasyon ayarları güncellendi, e-faturalar yeniden yükleniyor...');
      // Entegrasyon ayarları güncellendiğinde e-fatura listesini yeniden yükle
      loadEInvoices();
    }
  };

  // Firma Bilgileri Modalı Açma/Kapatma
  const handleOpenCompanyDetailsModal = () => {
    setIsCompanyDetailsModalOpen(true);
  };

  const handleCloseCompanyDetailsModal = (updated?: boolean) => {
    setIsCompanyDetailsModalOpen(false);
    if (updated) {
      console.log('Firma bilgileri güncellendi');
      // Burada gerekirse firma bilgilerini yeniden yükleyebiliriz
    }
  };

  // Kullanıcı Sorgulama Modalını Aç
  const handleOpenUserQueryModal = () => {
    handleCloseMoreMenu();
    
    if (!companyId) {
      setSnackbarMessage('Şirket bilgisi bulunamadı');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsUserQueryModalOpen(true);
  };

  const handleCloseUserQueryModal = () => {
    setIsUserQueryModalOpen(false);
  };
  
  // Arama fonksiyonu (gelen e-faturalar için)
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGelenEInvoices(gelenEInvoices);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = gelenEInvoices.filter(invoice => 
        (invoice.belgeNo || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.ettn || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.aliciUnvan || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.saticiUnvan || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.gonderenVknTckn || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.belgeTarihi || '').includes(searchTerm) ||
        (invoice.belgeTuru || '').toLowerCase().includes(lowerCaseSearchTerm)
      );
      setFilteredGelenEInvoices(filtered);
    }
  }, [searchTerm, gelenEInvoices]);

  // Arama fonksiyonu (giden e-faturalar için)
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGidenEInvoices(gidenEInvoices);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = gidenEInvoices.filter(invoice => 
        (invoice.yerelBelgeNo || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.ettn || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.belgeOid || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.aliciUnvan || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.alimDurumu || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.kullaniciKodu || '').toLowerCase().includes(lowerCaseSearchTerm) ||
        (invoice.alimZamani || '').includes(searchTerm) ||
        (invoice.hataMesaji || '').toLowerCase().includes(lowerCaseSearchTerm)
      );
      setFilteredGidenEInvoices(filtered);
    }
  }, [searchTerm, gidenEInvoices]);
  
  // Tab'a göre veri filtreleme ve sıralama (en yeni tarihli en üstte)
  const currentTabData = useMemo(() => {
    let data: EInvoiceData[] = [];
    
    if (currentTab === 0) {
      // Gelen E-Faturalar - her zaman array döndür
      data = Array.isArray(filteredGelenEInvoices) ? filteredGelenEInvoices : [];
      
      // ✅ Belge tarihine göre sıralama (en yeni en üstte)
      // QNB akışı YYYYMMDD, EDM akışı GG.AA.YYYY döndürür — ikisini de
      // sıralanabilir YYYYMMDD anahtarına çevir.
      const toSortKey = (d: string): string => {
        const m = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        return m ? `${m[3]}${m[2]}${m[1]}` : d;
      };
      data = [...data].sort((a, b) => {
        const dateA = toSortKey((a as GelenEInvoiceData).belgeTarihi || '00000000');
        const dateB = toSortKey((b as GelenEInvoiceData).belgeTarihi || '00000000');
        return dateB.localeCompare(dateA); // Descending (en yeni üstte)
      });
    } else {
      // Giden E-Faturalar - her zaman array döndür
      data = Array.isArray(filteredGidenEInvoices) ? filteredGidenEInvoices : [];
      
      // ✅ Alım zamanına göre sıralama (en yeni en üstte)
      data = [...data].sort((a, b) => {
        const alimZamaniA = (a as GidenEInvoiceData).alimZamani || '';
        const alimZamaniB = (b as GidenEInvoiceData).alimZamani || '';
        
        // DD/MM/YYYY HH:mm:ss (QNB/Pavo) veya GG.AA.YYYY (EDM) formatından Date objesine çevir
        const parseDate = (dateStr: string): Date => {
          if (!dateStr) return new Date(0);
          
          try {
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split(/[./]/);
            const [hours, minutes, seconds] = timePart ? timePart.split(':') : ['0', '0', '0'];
            
            return new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(hours),
              parseInt(minutes),
              parseInt(seconds)
            );
          } catch (error) {
            return new Date(0);
          }
        };
        
        const dateA = parseDate(alimZamaniA);
        const dateB = parseDate(alimZamaniB);
        
        return dateB.getTime() - dateA.getTime(); // Descending (en yeni üstte)
      });
    }
    
    return data;
  }, [currentTab, filteredGelenEInvoices, filteredGidenEInvoices]);
  
  // Tab değiştirme
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchTerm(''); // Arama terimini temizle

    // Tab değişiminde kullanıcının seçtiği tarih aralığı KORUNUR (otomatik 90 güne sıfırlanmaz).
    if (companyId) {
      // Veri listelerini temizle
      setGelenEInvoices([]);
      setGidenEInvoices([]);

      // ✅ YENİ: Tab değiştiğinde içeri aktarılan faturaları yeniden kontrol et (GELEN tab'ı için)
      if (newValue === 0) {
        checkImportedInvoices(companyId);
      }
    }
  };
  
  // Durum metni
  const getStatusText = (status: string): string => {
    if (status === EINVOICE_STATUS.DRAFT) return tt('Taslak', 'Draft');
    if (status === EINVOICE_STATUS.SENT) return tt('Gönderildi', 'Sent');
    if (status === EINVOICE_STATUS.DELIVERED) return tt('Teslim Edildi', 'Delivered');
    if (status === EINVOICE_STATUS.REJECTED) return tt('Reddedildi', 'Rejected');
    if (status === EINVOICE_STATUS.CANCELLED) return tt('İptal Edildi', 'Cancelled');
    return status;
  };
  
  // Durum rengi
  const getStatusColor = (status: string): string => {
    switch(status) {
      case EINVOICE_STATUS.DELIVERED: return 'success.main';
      case EINVOICE_STATUS.SENT: return 'info.main';
      case EINVOICE_STATUS.DRAFT: return 'warning.main';
      case EINVOICE_STATUS.REJECTED: return 'error.main';
      case EINVOICE_STATUS.CANCELLED: return 'text.disabled';
      default: return 'text.primary';
    }
  };
  
  // E-Faturayı görüntüleme (Detay modalını aç) - useCallback ile optimize et
  const handleViewEInvoice = useCallback(async (ettn: string, belgeNo?: string, invoiceType?: 'GELEN' | 'GIDEN') => {
    // ✅ EDM entegrasyonu → belgeyi EDM içerik ucundan (HTML) çekip görüntüleyicide göster
    if (eInvoiceProvider === 'edm') {
      const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;
      setPavoPayViewerOpen(true);
      setPavoPayViewerLoading(true);
      setPavoPayInvoiceHtml('');
      setPavoPayViewerInvoiceRef(belgeNo || ettn);
      try {
        const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_CONTENT, {
          uuid: ettn || null,
          id: belgeNo || null,
          type: 'HTML',
          // Giden belgeler EDM'de DIRECTION=OUT ile aranır; yön gönderilmezse
          // gelen kutusunda arama yapılır ve belge bulunamaz.
          direction: invoiceType === 'GIDEN' ? 'OUT' : 'IN',
          branchId,
        });
        const data = response.data;
        if (!data?.success || !data?.found || !data?.content) {
          setPavoPayInvoiceHtml(
            `<div style="padding:24px;font-family:sans-serif;color:#666;text-align:center;">${tt(
              'Bu faturanın görüntülenebilir belgesi bulunamadı.',
              'No viewable document found for this invoice.',
            )}</div>`,
          );
        } else {
          setPavoPayInvoiceHtml(data.content);

          // ✅ Gelen fatura başarıyla görüntülendiyse otomatik OKUNDU işaretle (sessiz, idempotent).
          if (invoiceType === 'GELEN') {
            einvoiceApi
              .post(EINVOICE_API.EDM_INBOX_MARK, { uuid: ettn || null, id: belgeNo || null, read: true, branchId })
              .then(() => {
                if (ettn) {
                  setInvoiceStatusMap((prev) => ({
                    ...prev,
                    [ettn]: { ...(prev[ettn] || {}), isRead: true },
                  }));
                }
              })
              .catch(() => { /* görüntüleme okundu işaretlemesi başarısız olsa da sessizce geç */ });
          }
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.errorLong ||
          err?.response?.data?.error ||
          err?.message ||
          tt('Belge yüklenemedi.', 'Failed to load document.');
        setPavoPayInvoiceHtml(`<div style="padding:24px;color:#d32f2f;font-family:sans-serif;">${msg}</div>`);
      } finally {
        setPavoPayViewerLoading(false);
      }
      return;
    }

    setSelectedEttn(ettn);
    setSelectedInvoiceNumber(belgeNo || null);
    setIsDetailModalOpen(true);
    console.log('E-Fatura detayı açılıyor:', { ettn, belgeNo });
  }, [eInvoiceProvider, branchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fatura detayı modalını kapatma - useCallback ile optimize et
  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedEttn(null);
    setSelectedInvoiceNumber(null);
  }, []);

  // PavoPay fatura HTML görüntüleme
  const handleViewPavoPayInvoice = useCallback(async (invoiceId: string, invoiceRef?: string) => {
    if (!activePavoPayBranchId && !hasGlobalPavoPayIntegration) {
      setPavoPayBranchWarningOpen(true);
      return;
    }
    setPavoPayViewerOpen(true);
    setPavoPayViewerLoading(true);
    setPavoPayInvoiceHtml('');
    setPavoPayViewerInvoiceRef(invoiceRef || invoiceId);
    try {
      const response = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
        Path: '/api/EInvoice/InvoiceViewer/GetHTMLInvoice',
        QueryString: `id=${invoiceId}`,
        Method: 'GET',
        BranchId: activePavoPayBranchId
      });
      const html = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      setPavoPayInvoiceHtml(html);
    } catch (err: any) {
      console.error('[PavoPay] Fatura HTML yüklenemedi:', err);
      setPavoPayInvoiceHtml(`<div style="padding:20px;color:red;font-family:sans-serif;">Fatura görüntülenemedi: ${err?.response?.data?.message || err?.message || 'Bilinmeyen hata'}</div>`);
    } finally {
      setPavoPayViewerLoading(false);
    }
  }, [accountingApi, activePavoPayBranchId, hasGlobalPavoPayIntegration]);

  // E-Faturayı düzenleme
  const handleEditEInvoice = (ettn: string) => {
    console.log('E-Fatura düzenle:', ettn);
  };
  
  // E-Faturayı silme
  const handleDeleteEInvoice = (ettn: string) => {
    console.log('E-Fatura sil:', ettn);
  };

  // HTML'i yazdırma için işleme (EInvoiceDetailModal'daki processHtmlForModal ile aynı)
  const processHtmlForPrint = (htmlContent: string): string => {
    // Yazdırma için optimize edilmiş CSS - modal CSS'inden farklı
    const printStyles = `
      <style>
        @page {
          size: A4;
          margin: 10mm;
        }
        body { 
          margin: 0;
          padding: 10mm;
          font-family: Arial, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #000;
          background: white;
          width: auto;
          min-width: 900px;
          box-sizing: border-box;
          overflow-x: visible;
        }
        * {
          box-sizing: border-box;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        td, th {
          padding: 4px;
          border: 1px solid #000;
          font-size: 11px;
        }
        /* Karekod ve resimler için güçlendirilmiş CSS */
        img {
          max-width: 100% !important;
          height: auto !important;
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
          border: none !important;
          background: transparent !important;
        }
        /* Base64 image desteği - QR kodlar için özel */
        img[src^="data:"] {
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
          min-width: 50px !important;
          min-height: 50px !important;
        }
        /* QR kod container'ları için */
        div[style*="qr"], .qr-code, [class*="qr"] {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        .no-break {
          page-break-inside: avoid;
        }
      </style>
    `;
    
    // Eğer HTML tam doküman değilse, minimal wrapper ekle
    if (!htmlContent.includes('<!DOCTYPE') && !htmlContent.includes('<html')) {
      return `
        <!DOCTYPE html>
        <html lang="tr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          ${printStyles}
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `;
    }
    
    // Tam HTML dokümanı ise, sadece charset ve print CSS'i ekle
    let processedHtml = htmlContent;
    
    // Head'e charset ve print CSS'i ekle
    if (processedHtml.includes('<head>')) {
      processedHtml = processedHtml.replace('<head>', `<head><meta charset="UTF-8">${printStyles}`);
    } else if (processedHtml.includes('<html>')) {
      processedHtml = processedHtml.replace('<html>', `<html><head><meta charset="UTF-8">${printStyles}</head>`);
    }
    
    return processedHtml;
  };

  // ✅ EDM içerik ucundan belge çek (gelen: DIRECTION=IN, giden: DIRECTION=OUT).
  // QNB uçları EDM credential'larını tanımadığı için EDM entegrasyonunda indirme/yazdırma bu yoldan yapılır.
  const fetchEdmDocument = async (
    ettn: string,
    type: 'XML' | 'HTML' | 'PDF',
    invoiceType: 'GELEN' | 'GIDEN',
  ): Promise<{ content: string; encoding?: string; contentType?: string; fileName?: string }> => {
    const branchId = branchFilter && branchFilter !== 'all' ? branchFilter : null;
    const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_CONTENT, {
      uuid: ettn,
      type,
      direction: invoiceType === 'GIDEN' ? 'OUT' : 'IN',
      branchId,
    });
    const data = response.data;
    if (!data?.success || !data?.found || !data?.content) {
      throw new Error(tt('Belge entegratörde bulunamadı.', 'Document not found at the integrator.'));
    }
    return data;
  };

  // ✅ EDM belgesini dosya olarak indir (UBL=XML · HTML · PDF).
  const downloadViaEdm = async (ettn: string, type: 'XML' | 'HTML' | 'PDF', invoiceType: 'GELEN' | 'GIDEN') => {
    const data = await fetchEdmDocument(ettn, type, invoiceType);
    let blob: Blob;
    if (data.encoding === 'base64') {
      const binary = atob(data.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      blob = new Blob([bytes], { type: data.contentType || 'application/octet-stream' });
    } else {
      blob = new Blob([data.content], { type: data.contentType || 'text/plain;charset=utf-8' });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.fileName || `e-fatura-${ettn}.${type.toLowerCase()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSnackbarMessage(tt(`✅ ${type === 'XML' ? 'UBL' : type} indirildi.`, `✅ ${type === 'XML' ? 'UBL' : type} downloaded.`));
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // E-Faturayı yazdırma (EInvoiceDetailModal.tsx'daki handlePrint ile aynı mantık)
  const handlePrintEInvoice = async (ettn: string, invoiceType: 'GELEN' | 'GIDEN' = 'GELEN') => {
    if (!ettn || !companyId) return;

    // ✅ EDM entegrasyonu → HTML'i EDM içerik ucundan çek ve yazdır (QNB'ye gitme)
    if (eInvoiceProvider === 'edm') {
      try {
        const data = await fetchEdmDocument(ettn, 'HTML', invoiceType);
        const processedHtml = processHtmlForPrint(data.content);
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Popup penceresi engellendi. Lütfen popup engelleyiciyi devre dışı bırakın.');
          return;
        }
        printWindow.document.write(processedHtml);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
      } catch (err: any) {
        const msg = err?.response?.data?.errorShort || err?.response?.data?.error || err?.message || tt('Bilinmeyen hata', 'Unknown error');
        setSnackbarMessage(tt('❌ Yazdırma için belge alınamadı: ', '❌ Failed to fetch document for printing: ') + msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      return;
    }
    
    try {
      console.log('🖨️ [E-INVOICE PRINT] SQL\'den entegrasyon bilgileri yükleniyor...', { companyId });

      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }
      
      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      console.log('✅ [E-INVOICE PRINT] Entegrasyon bilgileri SQL\'den yüklendi');
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        alert('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        return;
      }

      // HTML detay endpoint'ini oluştur (EInvoiceDetailModal'daki loadInvoiceDetail ile aynı)
      const endpoint = `${apiUrl}/QNBESolutions/GetFaturaDetay`;
      
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password,
        vkn: sqlData.taxNumber,
        ettn: ettn,
        format: 'HTML'
      });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      const apiResponse = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json,application/octet-stream,text/html,*/*',
        },
        mode: 'cors',
      });
      
      if (!apiResponse.ok) {
        const errorText = await apiResponse.text();
        console.error('Yazdırma için HTML detay API hatası:', errorText);
        throw new Error(`API hatası: ${apiResponse.status} - ${apiResponse.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Response'u işle (EInvoiceDetailModal'daki loadInvoiceDetail ile TAM AYNI mantık)
      const contentType = apiResponse.headers.get('content-type');
      let htmlContent = '';
      
      // Binary response ise (byte array olarak HTML)
      if (contentType && contentType.includes('application/octet-stream')) {
        const arrayBuffer = await apiResponse.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        
        try {
          // Byte array'i UTF-8 string'e çevir
          const decoder = new TextDecoder('utf-8');
          htmlContent = decoder.decode(uint8Array);
        } catch (decodeError) {
          // UTF-8 başarısız ise Latin1 dene
          try {
            const decoder = new TextDecoder('latin1');
            htmlContent = decoder.decode(uint8Array);
          } catch (latin1Error) {
            throw new Error('Byte array HTML\'ye çevrilemedi');
          }
        }
      }
      // PDF response ise
      else if (contentType && contentType.includes('application/pdf')) {
        throw new Error('PDF formatı yazdırma için desteklenmiyor. Lütfen HTML formatını kullanın.');
      }
      // Binary response ise (ZIP dosyası)
      else if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('application/zip'))) {
        const arrayBuffer = await apiResponse.arrayBuffer();
        
        try {
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          const zipContent = await zip.loadAsync(arrayBuffer);
          
          // HTML dosyasını bul
          let htmlFile = null;
          for (const fileName in zipContent.files) {
            if (fileName.toLowerCase().endsWith('.html')) {
              htmlFile = zipContent.files[fileName];
              break;
            }
          }
          
          if (htmlFile) {
            htmlContent = await htmlFile.async('text');
          } else {
            // HTML dosyası bulunamazsa ilk dosyayı al
            const firstFileName = Object.keys(zipContent.files)[0];
            if (firstFileName) {
              const firstFile = zipContent.files[firstFileName];
              htmlContent = await firstFile.async('text');
            } else {
              throw new Error('Binary ZIP dosyası boş.');
            }
          }
        } catch (binaryZipError) {
          throw new Error('Binary ZIP dosyası açılamadı: ' + (binaryZipError instanceof Error ? binaryZipError.message : String(binaryZipError)));
        }
      }
      // JSON response ise
      else if (contentType && contentType.includes('application/json')) {
        const jsonData = await apiResponse.json();
        
        // JSON içinde content alanı varsa onu kullan
        if (jsonData.content) {
          const content = jsonData.content;
          
          // HTML formatında byte array ise
          if (jsonData.format === 'HTML' && Array.isArray(content)) {
            try {
              // Array'i Uint8Array'e çevir
              const uint8Array = new Uint8Array(content);
              
              // Byte array'i UTF-8 string'e çevir
              const decoder = new TextDecoder('utf-8');
              htmlContent = decoder.decode(uint8Array);
            } catch (arrayDecodeError) {
              // UTF-8 başarısız ise Latin1 dene
              try {
                const uint8Array = new Uint8Array(content);
                const decoder = new TextDecoder('latin1');
                htmlContent = decoder.decode(uint8Array);
              } catch (latin1ArrayError) {
                throw new Error('JSON byte array HTML\'ye çevrilemedi');
              }
            }
          }
          // İlk olarak direkt HTML olup olmadığını kontrol et
          else if (typeof content === 'string' && (content.includes('<html') || content.includes('<!DOCTYPE'))) {
            htmlContent = content;
          }
          // Content'in ZIP dosyası olup olmadığını kontrol et (PK ile başlıyorsa)
          else if (typeof content === 'string' && content.startsWith('PK')) {
            try {
              // Farklı decode yöntemlerini dene
              let bytes: Uint8Array;
              
              // Yöntem 1: Latin1 encoding ile binary string decode
              try {
                const binaryString = content;
                bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i) & 0xFF;
                }
              } catch (latin1Error) {
                // Yöntem 2: Base64 decode dene
                try {
                  bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
                } catch (base64Error) {
                  // Yöntem 3: TextEncoder ile ArrayBuffer'a çevir
                  try {
                    const encoder = new TextEncoder();
                    const decoder = new TextDecoder('latin1');
                    const decodedString = decoder.decode(new Uint8Array(Array.from(content, c => c.charCodeAt(0))));
                    bytes = encoder.encode(decodedString);
                  } catch (textError) {
                    // Yöntem 4: Raw binary (son çare)
                    bytes = new Uint8Array(content.length);
                    for (let i = 0; i < content.length; i++) {
                      bytes[i] = content.charCodeAt(i) & 0xFF;
                    }
                  }
                }
              }
              
              const JSZip = (await import('jszip')).default;
              const zip = new JSZip();
              
              // JSZip'e farklı formatlarla yüklemeyi dene
              let zipContent;
              try {
                // Önce Uint8Array olarak dene
                zipContent = await zip.loadAsync(bytes);
              } catch (uint8Error) {
                try {
                  // ArrayBuffer olarak dene
                  const arrayBuffer = bytes.buffer as ArrayBuffer;
                  zipContent = await zip.loadAsync(arrayBuffer);
                } catch (bufferError) {
                  try {
                    // Raw string olarak dene (son çare)
                    zipContent = await zip.loadAsync(content, {base64: false});
                  } catch (stringError) {
                    throw new Error(`ZIP yükleme başarısız: Uint8Array(${uint8Error instanceof Error ? uint8Error.message : String(uint8Error)}), ArrayBuffer(${bufferError instanceof Error ? bufferError.message : String(bufferError)}), String(${stringError instanceof Error ? stringError.message : String(stringError)})`);
                  }
                }
              }
             
             // HTML dosyasını bul
             let htmlFile = null;
             for (const fileName in zipContent.files) {
               if (fileName.toLowerCase().endsWith('.html')) {
                 htmlFile = zipContent.files[fileName];
                 break;
               }
             }
             
             if (htmlFile) {
               htmlContent = await htmlFile.async('text');
             } else {
               // HTML dosyası bulunamazsa ilk dosyayı al
               const firstFileName = Object.keys(zipContent.files)[0];
               if (firstFileName) {
                 const firstFile = zipContent.files[firstFileName];
                 htmlContent = await firstFile.async('text');
               } else {
                 throw new Error('ZIP dosyası boş veya okunamıyor.');
               }
             }
            } catch (zipError) {
              throw new Error('JSON içindeki ZIP dosyası açılamadı: ' + (zipError instanceof Error ? zipError.message : String(zipError)));
            }
          } else {
            // Base64 decode edilmiş HTML içerik olabilir
            if (jsonData.format === 'HTML' && typeof content === 'string') {
              try {
                // Base64 decode et
                const binaryString = atob(content);
                
                // Türkçe karakter desteği için UTF-8 decode
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                
                // UTF-8 TextDecoder kullan
                const decoder = new TextDecoder('utf-8');
                htmlContent = decoder.decode(bytes);
              } catch (decodeError) {
                try {
                  // Fallback: Direkt atob kullan
                  htmlContent = atob(content);
                } catch (fallbackError) {
                  htmlContent = content;
                }
              }
            } else {
              // Direkt HTML content
              htmlContent = content;
            }
          }
        } else {
          // JSON'un kendisini string olarak göster
          throw new Error('JSON response\'da content alanı bulunamadı');
        }
      } else {
        // Normal text response (e.g., direct HTML)
        htmlContent = await apiResponse.text();
      }
      
      if (!htmlContent) {
        throw new Error('HTML içeriği alınamadı');
      }
      
      // HTML'i yazdırma için işle (processHtmlForPrint kullan)
      const processedHtml = processHtmlForPrint(htmlContent);
      
      // EInvoiceDetailModal'daki handlePrint ile aynı yazdırma mantığı
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Popup penceresi engellendi. Lütfen popup engelleyiciyi devre dışı bırakın.');
        return;
      }
      
      printWindow.document.write(processedHtml);
      printWindow.document.close();
      
      // Otomatik yazdırma dialog'unu aç (EInvoiceDetailModal ile aynı)
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      };
      
    } catch (error) {
      console.error('Yazdırma hatası:', error);
      alert('Yazdırma sırasında bir hata oluştu.');
    }
  };

  // Download menu işlemleri
  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>, ettn: string, invoiceType: 'GELEN' | 'GIDEN' = 'GELEN') => {
    event.stopPropagation();
    setDownloadMenuAnchor(event.currentTarget);
    setDownloadMenuEttn(ettn);
    setDownloadMenuInvoiceType(invoiceType);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setDownloadMenuEttn(null);
  };

  // PDF indirme (API endpoint kullanarak)
  const handleDownloadPdf = async (ettn: string, invoiceType: 'GELEN' | 'GIDEN' = 'GELEN') => {
    if (!ettn || !companyId) return;

    // ✅ EDM entegrasyonu → belgeyi EDM içerik ucundan indir (QNB'ye gitme)
    if (eInvoiceProvider === 'edm') {
      try {
        await downloadViaEdm(ettn, 'PDF', invoiceType);
      } catch (err: any) {
        const msg = err?.response?.data?.errorShort || err?.response?.data?.error || err?.message || tt('Bilinmeyen hata', 'Unknown error');
        setSnackbarMessage('❌ ' + tt('PDF indirme hatası: ', 'PDF download error: ') + msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      return;
    }
    
    try {
      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        alert('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        return;
      }

      // PDF indirme endpoint'ini oluştur
      const endpoint = `${apiUrl}/QNBESolutions/GelenBelgeleriIndir`;
      
      // Query parameters
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password,
        vkn: sqlData.taxNumber,
        format: 'PDF',
        belgeTuru: 'FATURA'
      });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      // POST request body - ETTN array
      const requestBody = [ettn];
      
      console.log('PDF indirme isteği gönderiliyor:', {
        endpoint: endpoint,
        params: {
          username: sqlData.username,
          password: '***',
          vkn: sqlData.taxNumber,
          format: 'PDF',
          belgeTuru: 'FATURA'
        },
        body: requestBody
      });
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf,application/octet-stream',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF indirme API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // File response'unu işle - direkt binary PDF dosyası bekleniyor
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      // Direkt binary PDF dosyası olarak işle
      const arrayBuffer = await response.arrayBuffer();
      console.log('PDF dosya boyutu:', arrayBuffer.byteLength, 'bytes');
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('Boş PDF dosyası alındı');
      }
      
      // PDF dosyası olarak indir
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('PDF başarıyla indirildi:', `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      
      // ✅ IDM/Download Manager müdahale ederse "Failed to fetch" gelir ama indirme başarılı olabilir
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        console.warn('⚠️ [PDF İNDİRME] Network hatası - IDM/Download Manager müdahale etmiş olabilir');
        // Kullanıcıya bilgi ver ama hata olarak gösterme (PDF muhtemelen indirildi)
        setSnackbarMessage('📥 PDF indirme başlatıldı. İndirmeler klasörünüzü kontrol edin.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return; // Hata fırlatma, çünkü indirme muhtemelen başarılı
      }
      
      // Diğer hatalar
      if (error instanceof Error && error.message.includes('CORS')) {
        setSnackbarMessage('❌ CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('❌ PDF indirme hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // HTML indirme (fatura detayını çekip HTML olarak indir)
  const handleDownloadHtml = async (ettn: string, invoiceType: 'GELEN' | 'GIDEN' = 'GELEN') => {
    if (!ettn || !companyId) return;

    // ✅ EDM entegrasyonu → belgeyi EDM içerik ucundan indir (QNB'ye gitme)
    if (eInvoiceProvider === 'edm') {
      try {
        await downloadViaEdm(ettn, 'HTML', invoiceType);
      } catch (err: any) {
        const msg = err?.response?.data?.errorShort || err?.response?.data?.error || err?.message || tt('Bilinmeyen hata', 'Unknown error');
        setSnackbarMessage('❌ ' + tt('HTML indirme hatası: ', 'HTML download error: ') + msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      return;
    }
    
    try {
      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        alert('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        return;
      }

      // HTML detay endpoint'ini oluştur
      const endpoint = `${apiUrl}/QNBESolutions/GetFaturaDetay`;
      
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password,
        vkn: sqlData.taxNumber,
        ettn: ettn,
        format: 'HTML'
      });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json,application/octet-stream,text/html,*/*',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTML detay API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Response'u işle (EInvoiceDetailModal'daki gibi)
      const contentType = response.headers.get('content-type');
      let htmlContent = '';
      
      if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();
        if (jsonData.content) {
          const content = jsonData.content;
          if (jsonData.format === 'HTML' && typeof content === 'string') {
            try {
              // Base64 decode et
              const binaryString = atob(content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const decoder = new TextDecoder('utf-8');
              htmlContent = decoder.decode(bytes);
            } catch (decodeError) {
              htmlContent = atob(content);
            }
          } else {
            htmlContent = content;
          }
        }
      } else {
        htmlContent = await response.text();
      }
      
      if (!htmlContent) {
        throw new Error('HTML içeriği alınamadı');
      }
      
      // HTML dosyası olarak indir
      const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('HTML başarıyla indirildi:', `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.html`);
      
    } catch (error) {
      console.error('HTML indirme hatası:', error);
      
      // ✅ IDM/Download Manager müdahale ederse "Failed to fetch" gelir ama indirme başarılı olabilir
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        console.warn('⚠️ [HTML İNDİRME] Network hatası - IDM/Download Manager müdahale etmiş olabilir');
        setSnackbarMessage('📥 HTML indirme başlatıldı. İndirmeler klasörünüzü kontrol edin.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return;
      }
      
      setSnackbarMessage('❌ HTML indirme hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDownloadPdfClick = () => {
    if (downloadMenuEttn) {
      handleDownloadMenuClose();
      handleDownloadPdf(downloadMenuEttn, downloadMenuInvoiceType);
    }
  };

  const handleDownloadHtmlClick = () => {
    if (downloadMenuEttn) {
      handleDownloadMenuClose();
      handleDownloadHtml(downloadMenuEttn, downloadMenuInvoiceType);
    }
  };

  // UBL indirme (API endpoint kullanarak)
  const handleDownloadUbl = async (ettn: string, invoiceType: 'GELEN' | 'GIDEN' = 'GELEN') => {
    if (!ettn || !companyId) return;

    // ✅ EDM entegrasyonu → ham UBL'yi (XML) EDM içerik ucundan indir (QNB'ye gitme)
    if (eInvoiceProvider === 'edm') {
      try {
        await downloadViaEdm(ettn, 'XML', invoiceType);
      } catch (err: any) {
        const msg = err?.response?.data?.errorShort || err?.response?.data?.error || err?.message || tt('Bilinmeyen hata', 'Unknown error');
        setSnackbarMessage('❌ ' + tt('UBL indirme hatası: ', 'UBL download error: ') + msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      return;
    }
    
    try {
      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        setSnackbarMessage('❌ E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // UBL indirme endpoint'ini oluştur
      const endpoint = `${apiUrl}/QNBESolutions/GelenBelgeleriIndir`;
      
      // Query parameters
      const params = new URLSearchParams({
        username: sqlData.username,
        password: sqlData.password,
        vkn: sqlData.taxNumber,
        format: 'UBL',
        belgeTuru: 'FATURA'
      });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      // POST request body - ETTN array
      const requestBody = [ettn];
      
      console.log('UBL indirme isteği gönderiliyor:', {
        endpoint: endpoint,
        params: {
          username: sqlData.username,
          password: '***',
          vkn: sqlData.taxNumber,
          format: 'UBL',
          belgeTuru: 'FATURA'
        },
        body: requestBody
      });
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/xml,application/octet-stream,*/*',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('UBL indirme API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // File response'unu işle - UBL XML dosyası bekleniyor
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      // UBL dosyası olarak işle
      const arrayBuffer = await response.arrayBuffer();
      console.log('UBL dosya boyutu:', arrayBuffer.byteLength, 'bytes');
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('Boş UBL dosyası alındı');
      }
      
      // UBL dosyası olarak indir (XML formatı)
      const blob = new Blob([arrayBuffer], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('UBL başarıyla indirildi:', `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.xml`);
      
    } catch (error) {
      console.error('UBL indirme hatası:', error);
      
      // ✅ IDM/Download Manager müdahale ederse "Failed to fetch" gelir ama indirme başarılı olabilir
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        console.warn('⚠️ [UBL İNDİRME] Network hatası - IDM/Download Manager müdahale etmiş olabilir');
        setSnackbarMessage('📥 UBL/XML indirme başlatıldı. İndirmeler klasörünüzü kontrol edin.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return; // Hata fırlatma, çünkü indirme muhtemelen başarılı
      }
      
      // Diğer hatalar
      if (error instanceof Error && error.message.includes('CORS')) {
        setSnackbarMessage('❌ CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('❌ UBL indirme hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleDownloadUblClick = () => {
    if (downloadMenuEttn) {
      handleDownloadMenuClose();
      handleDownloadUbl(downloadMenuEttn, downloadMenuInvoiceType);
    }
  };
  
  // Para formatı
  const formatCurrency = (amount: number | undefined, currencyCode: string): string => {
    if (typeof amount !== 'number') return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currencyCode 
    }).format(amount);
  };
  
  // Tarih formatı (YYYYMMDD formatından DD.MM.YYYY formatına çevir)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    
    try {
      // GG.AA.YYYY zaten Türkçe format (EDM akışı böyle doldurur) — olduğu gibi döndür.
      // new Date("05.08.2026") bunu AA.GG.YYYY (ABD) sayıp gün/ayı ters çevirdiği için
      // aşağıdaki genel parse'a düşürülmemeli.
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) return dateString;

      // YYYYMMDD formatından Date objesine çevir
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        
        const date = new Date(`${year}-${month}-${day}`);
        if (isNaN(date.getTime())) return dateString; // Geçersiz tarih ise orijinal değeri döndür
        
        return new Intl.DateTimeFormat('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(date);
      }
      
      // Diğer formatlar için standart Date parsing
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return new Intl.DateTimeFormat('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return dateString;
    }
  };
  
  // ETTN kopyalama fonksiyonu
  const handleCopyETTN = (ettn: string) => {
    navigator.clipboard.writeText(ettn).then(() => {
      setSnackbarMessage('✅ ETTN kopyalandı!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('ETTN kopyalama hatası:', err);
      setSnackbarMessage('❌ ETTN kopyalanamadı');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    });
  };
  
  // Sütun değerini render fonksiyonlarına dönüştür
  const invoiceColumns = useMemo<DataTableColumn<EInvoiceData>[]>(() => {
    return tableColumns
      .filter(col => col.visible)
      .map(col => {
        const base: DataTableColumn<EInvoiceData> = {
          id: col.id,
          label: col.label,
          field: col.field as keyof EInvoiceData,
          sortable: col.id !== 'actions' && col.id !== 'rowNumber',
        };
        switch (col.id) {
          case 'rowNumber':
            return { ...base, render: (_row: EInvoiceData, index?: number) => index !== undefined ? index + 1 : '-' };
          case 'belgeNo':
            return { ...base, render: (row: EInvoiceData) => 'belgeNo' in row ? (row as GelenEInvoiceData).belgeNo || '-' : '-' };
          case 'saticiUnvan':
            return { ...base, render: (row: EInvoiceData) => {
              if (!('belgeNo' in row)) return '-';
              const unvan = (row as GelenEInvoiceData).saticiUnvan || '-';
              return (
                <Tooltip title={unvan}>
                  <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {unvan}
                  </Typography>
                </Tooltip>
              );
            }};
          case 'belgeTarihi':
            return { ...base, render: (row: EInvoiceData) => 'belgeNo' in row ? formatDate((row as GelenEInvoiceData).belgeTarihi) : '-' };
          case 'belgeTuru':
            return { ...base, render: (row: EInvoiceData) => 'belgeNo' in row ? <Typography variant="body2">{(row as GelenEInvoiceData).belgeTuru || '-'}</Typography> : '-' };
          case 'faturaProfili':
            return { ...base, render: (row: EInvoiceData) => {
              if (!('belgeNo' in row)) return '-';
              const ettn = (row as GelenEInvoiceData).ettn;
              const statusInfo = invoiceStatusMap[ettn];
              if (!statusInfo) return <Typography variant="body2" color="text.secondary">Sorgulanıyor...</Typography>;
              if (statusInfo.error) return <Typography variant="body2" color="text.secondary">Bilinmiyor</Typography>;
              const yanitDurumu = statusInfo.yanitDurumu;
              return <Typography variant="body2">{yanitDurumu === -1 ? 'Temel Fatura' : 'Ticari Fatura'}</Typography>;
            }};
          case 'gonderenVknTckn':
            return { ...base, render: (row: EInvoiceData) => 'belgeNo' in row ? (row as GelenEInvoiceData).gonderenVknTckn || '-' : '-' };
          case 'toplamTutar':
            return { ...base, render: (row: EInvoiceData) => {
              if (!('belgeNo' in row)) return <Typography variant="body2" color="text.secondary">-</Typography>;
              const gelenInvoice = row as GelenEInvoiceData;
              const toplamTutar = gelenInvoice.odenecekTutar;
              const paraBirimi = gelenInvoice.odenecekTutarDovizCinsi || 'TRY';
              if (toplamTutar) {
                try {
                  const numericValue = parseFloat(String(toplamTutar).replace(',', '.'));
                  if (!isNaN(numericValue)) {
                    return <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(numericValue, paraBirimi)}</Typography>;
                  }
                } catch (error) { /* ignore */ }
              }
              return <Typography variant="body2" color="text.secondary">-</Typography>;
            }};
          case 'yerelBelgeNo':
            return { ...base, render: (row: EInvoiceData) => !('belgeNo' in row) ? (row as GidenEInvoiceData).yerelBelgeNo || '-' : '-' };
          case 'belgeOid':
            return { ...base, render: (row: EInvoiceData) => !('belgeNo' in row) ? (row as GidenEInvoiceData).belgeOid || '-' : '-' };
          case 'aliciUnvan':
            return { ...base, render: (row: EInvoiceData) => {
              if ('belgeNo' in row) return '-';
              const unvan = (row as GidenEInvoiceData).aliciUnvan || '-';
              return (
                <Tooltip title={unvan}>
                  <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {unvan}
                  </Typography>
                </Tooltip>
              );
            }};
          case 'gidenTutar':
            return { ...base, render: (row: EInvoiceData) => {
              if ('belgeNo' in row) return '-';
              const giden = row as GidenEInvoiceData;
              if (giden.odenecekTutar) {
                const numericValue = parseFloat(String(giden.odenecekTutar).replace(',', '.'));
                if (!isNaN(numericValue)) {
                  return <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(numericValue, giden.odenecekTutarDovizCinsi || 'TRY')}</Typography>;
                }
              }
              return <Typography variant="body2" color="text.secondary">-</Typography>;
            }};
          case 'gidenProfil':
            return { ...base, render: (row: EInvoiceData) => {
              if ('belgeNo' in row) return '-';
              const profile = ((row as GidenEInvoiceData).profileId || '').toUpperCase();
              if (!profile) return <Typography variant="body2" color="text.secondary">-</Typography>;
              const label = profile === 'TICARIFATURA' ? tt('Ticari', 'Commercial')
                : profile === 'TEMELFATURA' ? tt('Temel', 'Basic')
                : profile.includes('EARSIV') ? 'e-Arşiv'
                : profile;
              return <Typography variant="body2">{label}</Typography>;
            }};
          case 'alimZamani':
            return { ...base, render: (row: EInvoiceData) => !('belgeNo' in row) ? (row as GidenEInvoiceData).alimZamani || '-' : '-' };
          case 'alimDurumu':
            return { ...base, render: (row: EInvoiceData) => {
              if ('belgeNo' in row) return '-';
              return <Chip label={(row as GidenEInvoiceData).alimDurumu || '-'} size="small" color={(row as GidenEInvoiceData).alimDurumu === 'İşlendi' ? 'success' : 'error'} />;
            }};
          case 'kullaniciKodu':
            return { ...base, render: (row: EInvoiceData) => !('belgeNo' in row) ? (row as GidenEInvoiceData).kullaniciKodu || '-' : '-' };
          case 'kaynak':
            return { ...base, render: (row: EInvoiceData) => !('belgeNo' in row) ? (row as GidenEInvoiceData).kaynak || '-' : '-' };
          case 'hataMesaji':
            return { ...base, render: (row: EInvoiceData) => {
              if ('belgeNo' in row) return '-';
              const mesaj = (row as GidenEInvoiceData).hataMesaji || '-';
              return (
                <Tooltip title={mesaj}>
                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mesaj}</Typography>
                </Tooltip>
              );
            }};
          case 'ettn':
            return { ...base, render: (row: EInvoiceData) => {
              const ettnValue = row.ettn || '-';
              if (ettnValue === '-') return '-';
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={ettnValue}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ettnValue}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="ETTN'yi kopyala">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopyETTN(ettnValue); }} sx={{ padding: '2px' }}>
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            }};
          case 'durum':
            return { ...base, render: (row: EInvoiceData) => {
              const isGiden = !('belgeNo' in row);
              if (isGiden && (row as GidenEInvoiceData).kaynak === 'PavoPay') {
                const durumText = (row as GidenEInvoiceData).alimDurumu || '-';
                const isSuccess = durumText.toLowerCase().includes('başarı') || durumText.toLowerCase().includes('tamamlan');
                const isError = durumText.toLowerCase().includes('hata') || durumText.toLowerCase().includes('red');
                const chipColor = isSuccess ? 'success' : isError ? 'error' : 'info';
                return <Chip label={durumText} size="small" color={chipColor} />;
              }
              const ettn = row.ettn;
              if (!ettn) return '-';
              const statusInfo = invoiceStatusMap[ettn];
              if (!statusInfo) return <Chip label={tt('Sorgulanıyor...', 'Querying...')} size="small" color="default" variant="outlined" />;
              if (statusInfo.error) return <Tooltip title={statusInfo.error}><Chip label={tt('Hata', 'Error')} size="small" color="error" variant="outlined" /></Tooltip>;
              const yanitDurumu = statusInfo.yanitDurumu;
              let label = '-';
              let color: 'default' | 'warning' | 'error' | 'success' | 'info' = 'default';
              if (yanitDurumu === 0) { label = tt('Yanıt Bekleniyor', 'Awaiting Response'); color = 'warning'; }
              else if (yanitDurumu === 1) { label = tt('Reddedildi', 'Rejected'); color = 'error'; }
              else if (yanitDurumu === 2) { label = tt('Kabul Edildi', 'Accepted'); color = 'success'; }
              else if (yanitDurumu === -1) { label = tt('Temel Fatura', 'Basic Invoice'); color = 'info'; }
              return <Chip label={label} size="small" color={color} />;
            }};
          case 'okunma':
            return { ...base, render: (row: EInvoiceData) => {
              // Yalnızca gelen faturalarda okundu/okunmadı bilgisi vardır
              if (!('belgeNo' in row)) return '-';
              const ettnForRead = (row as GelenEInvoiceData).ettn;
              const statusInfo = ettnForRead ? invoiceStatusMap[ettnForRead] : null;
              // EDM dışı / bilgi yoksa gösterme
              if (!statusInfo || statusInfo.isRead === undefined) return <Typography variant="body2" color="text.secondary">-</Typography>;
              const isRead = !!statusInfo.isRead;
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {isRead ? (
                    <Tooltip title={tt('Okundu', 'Read')}>
                      <MarkEmailReadIcon sx={{ color: 'text.disabled', fontSize: 22 }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={tt('Okunmadı', 'Unread')}>
                      <Chip
                        icon={<MarkEmailUnreadIcon sx={{ fontSize: 16 }} />}
                        label={tt('Yeni', 'New')}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      />
                    </Tooltip>
                  )}
                </Box>
              );
            }};
          case 'imported':
            return { ...base, render: (row: EInvoiceData) => {
              if (!('belgeNo' in row)) return '-';
              const ettnForImport = (row as GelenEInvoiceData).ettn;
              const isImported = ettnForImport && importedInvoiceETTNs.has(ettnForImport);
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {isImported ? (
                    <Tooltip title={tt('Bu e-fatura sisteme içeri aktarılmış', 'This e-invoice has been imported into the system')}><CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} /></Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </Box>
              );
            }};
          case 'actions':
            return { ...base, render: (row: EInvoiceData) => {
              const isGelenEInvoice = 'belgeNo' in row;
              const ettn = row.ettn;
              const belgeNo = isGelenEInvoice ? (row as GelenEInvoiceData).belgeNo : (row as GidenEInvoiceData).yerelBelgeNo;
              const invoiceType: 'GELEN' | 'GIDEN' = isGelenEInvoice ? 'GELEN' : 'GIDEN';
              const kaynak = !isGelenEInvoice ? (row as GidenEInvoiceData).kaynak : '';
              const belgeOid = !isGelenEInvoice ? (row as GidenEInvoiceData).belgeOid : '';
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {(ettn || kaynak === 'PavoPay') && (
                    <Tooltip title={tt('İşlemler', 'Actions')}>
                      <IconButton size="small" onClick={(e) => handleOpenActionsMenu(e, ettn || '', belgeNo, invoiceType, kaynak, belgeOid)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            }};
          default:
            return base;
        }
      });
  }, [tableColumns, invoiceStatusMap, importedInvoiceETTNs]);
  
  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    setLoadingExport(true);
    // Excel export logic burada olacak
    setTimeout(() => {
      setLoadingExport(false);
      console.log('Excel export tamamlandı');
    }, 2000);
  };

  return (
    <>
      {/* Üst Başlık ve Butonlar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {tt('E-Fatura Yönetimi', 'E-Invoice Management')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* EDM kalan kontör göstergesi (tıklayınca yenilenir) */}
            {(eInvoiceProvider === 'edm' || kontorProviderIsEdm) && (kontorLoading || kontorLeft !== null || kontorError) && (
              <Tooltip
                title={kontorError
                  ? `${kontorError} — ${tt('Tekrar denemek için tıklayın.', 'Click to retry.')}`
                  : tt(
                      'EDM hesabınızda kalan e-fatura/e-arşiv kontörü. Yenilemek için tıklayın.',
                      'Remaining e-invoice/e-archive credits in your EDM account. Click to refresh.'
                    )}
                arrow
              >
                <Chip
                  icon={kontorLoading
                    ? <CircularProgress size={14} color="inherit" />
                    : <RefreshIcon fontSize="small" />}
                  label={kontorLoading
                    ? tt('Kontör sorgulanıyor...', 'Checking credits...')
                    : kontorError
                      ? tt('Kontör alınamadı', 'Credits unavailable')
                      : tt(
                          `Kalan Kontör: ${kontorLeft!.toLocaleString('tr-TR')}`,
                          `Credits Left: ${kontorLeft!.toLocaleString('en-US')}`
                        )}
                  color={kontorError
                    ? 'default'
                    : kontorLeft !== null && kontorLeft < 100 ? 'warning' : 'success'}
                  variant="outlined"
                  onClick={kontorLoading ? undefined : loadKontor}
                  sx={{ fontWeight: 'bold' }}
                />
              </Tooltip>
            )}
            <ButtonGroup>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<PersonSearchIcon />}
              onClick={handleOpenUserQueryModal}
            >
              {tt('Kullanıcı Sorgula', 'Query User')}
            </Button>
            <Button 
              variant="outlined"
              onClick={handleOpenMoreMenu}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              <MoreVertIcon />
            </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {/* Arama Alanı ve Ayarlar Butonları */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md="auto">
            <TextField
              size="small"
              placeholder={tt('E-Fatura Ara...', 'Search E-Invoice...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', md: 300 } }}
            />
          </Grid>
          
          {/* Tarih aralığı seçici (hem gelen hem giden e-faturalar için) */}
          {(currentTab === 0 || currentTab === 1) && (
            <>
              <Grid item xs={12} md="auto">
                <AppDatePicker
                  size="small"
                  label={tt('Başlangıç Tarihi', 'Start Date')}
                  value={startDate ? `${startDate.substring(0,4)}-${startDate.substring(4,6)}-${startDate.substring(6,8)}` : ''}
                  onChange={(value) => {
                    if (!value) { setStartDate(''); return; }
                    // Sadece seçilen başlangıç tarihini ayarla; bitiş tarihine dokunma.
                    setStartDate(value.replace(/-/g, '')); // YYYYMMDD
                  }}
                  sx={{ width: { xs: '100%', md: 180 } }}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <AppDatePicker
                  size="small"
                  label={tt('Bitiş Tarihi', 'End Date')}
                  value={endDate ? `${endDate.substring(0,4)}-${endDate.substring(4,6)}-${endDate.substring(6,8)}` : ''}
                  onChange={(value) => {
                    if (!value) { setEndDate(''); return; }
                    // Sadece seçilen bitiş tarihini ayarla; başlangıç tarihine dokunma.
                    setEndDate(value.replace(/-/g, '')); // YYYYMMDD
                  }}
                  sx={{ width: { xs: '100%', md: 180 } }}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={() => {
                    if (currentTab === 0) {
                      loadEInvoices(); // Gelen e-faturalar
                    } else {
                      loadGidenEInvoices(); // Giden e-faturalar
                    }
                  }}
                  disabled={loading || !companyId || !startDate || !endDate}
                  size="small"
                  fullWidth={true}
                  sx={{ 
                    display: { xs: 'block', md: 'inline-flex' },
                    width: { xs: '100%', md: 'auto' }
                  }}
                >
                  {loading ? tt('Filtreleniyor...', 'Filtering...') : tt('Filtrele', 'Filter')}
                </Button>
              </Grid>
              {/* Sadece okunmamış faturalar (yalnızca Gelen kutusu / EDM) */}
              {currentTab === 0 && eInvoiceProvider === 'edm' && (
                <Grid item xs={12} md="auto">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={unreadOnly}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setUnreadOnly(next);
                          if (companyId && startDate && endDate) loadEInvoices(next);
                        }}
                      />
                    }
                    label={tt('Sadece okunmamış', 'Unread only')}
                  />
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={[
                ...(isManager ? [] : [{ id: 'all', label: tt('Tümü', 'All') }]),
                ...Object.entries(branchMap).map(([id, name]) => ({ id, label: name }))
              ]}
              value={
                branchFilter === 'all' ? { id: 'all', label: tt('Tümü', 'All') } :
                branchMap[branchFilter] ? { id: branchFilter, label: branchMap[branchFilter] } : null
              }
              onChange={(_, newValue) => {
                setBranchFilter(newValue?.id || 'all');
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={isManager}
              disableClearable={isManager}
              renderInput={(params) => <TextField {...params} label={tt('Şube', 'Branch')} />}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tab'lar - Tablonun hemen üstünde */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ 
            minHeight: 48, // Dikey olarak kısalt
            '& .MuiTab-root': {
              minHeight: 48, // Tab yüksekliğini azalt
              fontSize: '0.875rem', // Font boyutunu küçült
              padding: '8px 16px' // Padding'i azalt
            }
          }}
        >
          <Tab 
            label={tt('Gelen E-Faturalar', 'Incoming E-Invoices')} 
            icon={<ReceiptIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab 
            label={tt('Giden E-Faturalar', 'Outgoing E-Invoices')} 
            icon={<SendIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : currentTabData.length === 0 ? (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <DescriptionIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {currentTab === 0 ? tt('Gelen E-Fatura Listesi', 'Incoming E-Invoice List') : tt('Giden E-Fatura Listesi', 'Outgoing E-Invoice List')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm ? tt("Arama sonucu bulunamadı", "No results found") : 
             currentTab === 0 ? tt("Seçilen tarih aralığında gelen e-fatura bulunamadı. Farklı bir tarih aralığı deneyebilirsiniz.", "No incoming e-invoices found in the selected date range. You can try a different date range.") : 
             tt("Seçilen tarih aralığında giden e-fatura bulunamadı. Farklı bir tarih aralığı deneyebilirsiniz.", "No outgoing e-invoices found in the selected date range. You can try a different date range.")}
          </Typography>
          
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNewInvoiceMenu}
          >
            {tt('Yeni E-Fatura Oluştur', 'Create New E-Invoice')}
          </Button>
        </Box>
      ) : (
        <AccountingDataTable<EInvoiceData>
          columns={invoiceColumns}
          rows={currentTabData}
          getRowId={(row) => row.ettn || `invoice-${Math.random()}`}
          loading={false}
          defaultRowsPerPage={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage={searchTerm ? tt("Arama sonucu bulunamadı", "No results found") : currentTab === 0 ? tt("Seçilen tarih aralığında gelen e-fatura bulunamadı", "No incoming e-invoices found in selected date range") : tt("Seçilen tarih aralığında giden e-fatura bulunamadı", "No outgoing e-invoices found in selected date range")}
          emptyIcon={<ReceiptIcon sx={{ fontSize: 48 }} />}
          ariaLabel={currentTab === 0 ? tt('Gelen e-faturalar tablosu', 'Incoming e-invoices table') : tt('Giden e-faturalar tablosu', 'Outgoing e-invoices table')}
          columnResizeStorageKey="e-invoice"
        />
      )}

      {/* Entegrasyon Ayarları Modalı */}
      <EInvoiceIntegrationModal
        open={isIntegrationModalOpen}
        onClose={handleCloseIntegrationModal}
        companyId={companyId}
      />

      {/* Tasarım Şablonları (XSLT) Modalı */}
      <XsltTemplateModal
        open={isXsltTemplateModalOpen}
        onClose={() => setIsXsltTemplateModalOpen(false)}
      />

      {/* Firma Bilgileri Modalı */}
      <CompanyDetailsModal
        open={isCompanyDetailsModalOpen}
        onClose={handleCloseCompanyDetailsModal}
        companyId={companyId}
      />

      {/* Kullanıcı Sorgulama Modalı */}
      <EInvoiceUserQueryModal
        open={isUserQueryModalOpen}
        onClose={handleCloseUserQueryModal}
        companyId={companyId || ''}
      />

      {/* E-Fatura Detayı Modalı */}
      <EInvoiceDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        ettn={selectedEttn}
        companyId={companyId}
        invoiceNumber={selectedInvoiceNumber || undefined}
      />

      {/* E-Fatura Durum Sorgula Modalı */}
      <EInvoiceStatusQueryModal
        open={isStatusQueryModalOpen}
        onClose={handleCloseStatusQueryModal}
        ettn={statusQueryEttn}
        belgeNo={statusQueryBelgeNo}
        companyId={companyId}
        invoiceType={statusQueryInvoiceType} // ✅ Fatura yönü (GELEN/GIDEN)
      />

      {/* E-Fatura Tarihçe Modalı */}
      <EInvoiceHistoryModal
        open={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        ettn={historyEttn || ''}
        invoiceType={historyInvoiceType}
        companyId={companyId || ''}
      />

      {/* Kabul/Red Yanıt Modalı */}
      <InvoiceResponseModal
        open={isResponseModalOpen}
        onClose={handleCloseResponseModal}
        ettn={responseEttn || ''}
        belgeNo={responseBelgeNo || ''}
        responseType={responseType}
        companyId={companyId || ''}
        onSuccess={(type, ettn) => {
          const yd = type === 'KABUL' ? 2 : 1;
          // Yanıt başarılı → listedeki durumu anında güncelle (GİB işleyene kadar)
          setInvoiceStatusMap((prev) => ({
            ...prev,
            [ettn]: {
              ...(prev[ettn] || {}),
              yanitDurumu: yd,
              durumAciklama: type === 'KABUL' ? 'Kabul Edildi' : 'Reddedildi',
            },
          }));
          // Liste yenilense bile (GİB henüz işlemediyse) durum geri dönmesin
          setRespondedOverrides((prev) => ({ ...prev, [ettn]: yd }));
        }}
      />

      {/* Yeni E-Fatura Menüsü */}
      <Menu
        anchorEl={anchorElNewInvoice}
        open={Boolean(anchorElNewInvoice)}
        onClose={handleCloseNewInvoiceMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleNewSalesInvoice}>
          <ListItemIcon>
            <ShoppingBagIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('Satış Faturası', 'Sales Invoice')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNewPurchaseInvoice}>
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('Alış Faturası', 'Purchase Invoice')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Üst Menü - 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseMoreMenu();
          }}
          disabled={loadingExport || loading || currentTabData.length === 0}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}</ListItemText>
        </MenuItem>
        {!isManager && (
          <>
            <Divider />
            <MenuItem onClick={() => { handleOpenCompanyDetailsModal(); handleCloseMoreMenu(); }}>
              <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tt('Firma Bilgileri', 'Company Details')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleOpenIntegrationModal(); handleCloseMoreMenu(); }}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tt('Entegrasyon Ayarları', 'Integration Settings')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setIsXsltTemplateModalOpen(true); handleCloseMoreMenu(); }}>
              <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tt('Tasarım Şablonları', 'Design Templates')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { navigate('/e-fatura/pavopay-entegrasyonlar'); handleCloseMoreMenu(); }}>
              <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tt('PavoPay Entegrasyonları', 'PavoPay Integrations')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* İşlemler (Actions) Menüsü */}
      <Menu
        anchorEl={anchorElActions}
        open={Boolean(anchorElActions)}
        onClose={handleCloseActionsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 10000,
          '& .MuiPaper-root': {
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedActionKaynak === 'PavoPay' && selectedActionBelgeOid) {
              handleViewPavoPayInvoice(selectedActionBelgeOid, selectedActionBelgeNo || undefined);
              handleCloseActionsMenu();
            } else if (selectedActionEttn) {
              handleViewEInvoice(selectedActionEttn, selectedActionBelgeNo || '', selectedActionInvoiceType);
              handleCloseActionsMenu();
            }
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{selectedActionKaynak === 'PavoPay' ? tt('PavoPay Önizleme', 'PavoPay Preview') : tt('Detayları Görüntüle', 'View Details')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            if (selectedActionEttn) {
              handleDownloadMenuOpen(e, selectedActionEttn, selectedActionInvoiceType);
              handleCloseActionsMenu();
            }
          }}
        >
          <ListItemIcon>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('İndir', 'Download')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedActionEttn) {
              handlePrintEInvoice(selectedActionEttn, selectedActionInvoiceType);
              handleCloseActionsMenu();
            }
          }}
        >
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('Yazdır', 'Print')}</ListItemText>
        </MenuItem>
        {/* ✅ İçeri Aktar - Sadece GELEN faturalar için */}
        {selectedActionInvoiceType === 'GELEN' && (() => {
          // Durum bilgisini al
          const statusInfo = selectedActionEttn ? invoiceStatusMap[selectedActionEttn] : null;
          const yanitDurumu = statusInfo?.yanitDurumu;
          const isTemelFatura = yanitDurumu === -1;
          const isYanitBekleniyor = yanitDurumu === 0;
          const isReddedildi = yanitDurumu === 1;
          const isKabulEdildi = yanitDurumu === 2;
          
          // ✅ YENİ: Zaten içeri aktarılmış mı kontrol et
          const isAlreadyImported = selectedActionEttn && importedInvoiceETTNs.has(selectedActionEttn);
          
          // Tooltip mesajı
          let tooltipMessage = '';
          let isDisabled = false;
          
          if (isAlreadyImported) {
            // Zaten içeri aktarılmış
            isDisabled = true;
            tooltipMessage = tt('Bu e-fatura zaten sisteme içeri aktarılmış', 'This e-invoice has already been imported');
          } else if (!isTemelFatura) {
            // Ticari fatura
            if (isYanitBekleniyor) {
              isDisabled = true;
              tooltipMessage = tt('Lütfen faturaya yanıt veriniz', 'Please respond to this invoice');
            } else if (isReddedildi) {
              isDisabled = true;
              tooltipMessage = tt('Fatura reddedildiği için içeri aktarılamaz', 'Invoice cannot be imported because it was rejected');
            } else if (isKabulEdildi) {
              isDisabled = false;
              tooltipMessage = tt('Faturayı içeri aktar', 'Import invoice');
            }
          } else {
            // Temel fatura - her zaman aktif (ama içeri aktarılmamışsa)
            isDisabled = false;
            tooltipMessage = tt('Faturayı içeri aktar', 'Import invoice');
          }
          
          return (
            <Tooltip title={tooltipMessage} placement="left">
              <span>
                <MenuItem 
                  onClick={() => {
                    if (!isDisabled && selectedActionEttn) {
                      handleImportEInvoice(selectedActionEttn); // ✅ E-faturayı içe aktar
                      handleCloseActionsMenu();
                    }
                  }}
                  disabled={isDisabled}
                  sx={{
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <ListItemIcon>
                    <CloudUploadIcon fontSize="small" sx={{ opacity: isDisabled ? 0.5 : 1 }} />
                  </ListItemIcon>
                  <ListItemText>{tt('İçeri Aktar', 'Import')}</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          );
        })()}
        <MenuItem 
          onClick={() => {
            if (selectedActionEttn && selectedActionBelgeNo) {
              handleOpenStatusQueryModal(selectedActionEttn, selectedActionBelgeNo, selectedActionInvoiceType); // ✅ Fatura yönünü gönder
              handleCloseActionsMenu();
            }
          }}
        >
          <ListItemIcon>
            <SearchIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>{tt('Durum Sorgula', 'Query Status')}</ListItemText>
        </MenuItem>        <MenuItem 
          onClick={() => {
            if (selectedActionEttn) {
              handleOpenHistoryModal(selectedActionEttn, selectedActionInvoiceType); // ✅ Fatura yönünü gönder
              handleCloseActionsMenu();
            }
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>{tt('Tarihçe', 'History')}</ListItemText>
        </MenuItem>
        
        {/* ✅ YENİ: Kabul Et / Reddet - Sadece GELEN Ticari Faturalar için */}
        {selectedActionInvoiceType === 'GELEN' && selectedActionEttn && (() => {
          const statusInfo = invoiceStatusMap[selectedActionEttn];
          const isTemelFatura = statusInfo?.yanitDurumu === -1;
          const isYanitVerilmemis = statusInfo?.yanitDurumu === 0;
          
          // Sadece ticari faturalar için göster (yanitDurumu !== -1)
          // Ve henüz yanıt verilmemişse göster (yanitDurumu === 0)
          if (!isTemelFatura && isYanitVerilmemis) {
            return (
              <>
                <MenuItem 
                  onClick={() => {
                    if (selectedActionEttn && selectedActionBelgeNo) {
                      handleOpenResponseModal(selectedActionEttn, selectedActionBelgeNo, 'KABUL');
                    }
                  }}
                  sx={{ color: 'success.main' }}
                >
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText>{tt('Kabul Et', 'Accept')}</ListItemText>
                </MenuItem>
                <MenuItem 
                  onClick={() => {
                    if (selectedActionEttn && selectedActionBelgeNo) {
                      handleOpenResponseModal(selectedActionEttn, selectedActionBelgeNo, 'RED');
                    }
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <CancelIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>{tt('Reddet', 'Reject')}</ListItemText>
                </MenuItem>
              </>
            );
          }
          return null;
        })()}

        {/* ✅ Okundu / Okunmadı işaretle — Sadece GELEN faturalar (EDM) için */}
        {selectedActionInvoiceType === 'GELEN' && selectedActionEttn && eInvoiceProvider === 'edm' && (() => {
          const isRead = !!invoiceStatusMap[selectedActionEttn]?.isRead;
          const ettn = selectedActionEttn;
          const belgeNo = selectedActionBelgeNo;
          return (
            <MenuItem
              disabled={markingRead}
              onClick={() => {
                handleCloseActionsMenu();
                handleMarkInboxRead(ettn, belgeNo, !isRead);
              }}
            >
              <ListItemIcon>
                {isRead
                  ? <MarkEmailUnreadIcon fontSize="small" color="action" />
                  : <MarkEmailReadIcon fontSize="small" color="primary" />}
              </ListItemIcon>
              <ListItemText>
                {isRead
                  ? tt('Okunmadı olarak işaretle', 'Mark as unread')
                  : tt('Okundu olarak işaretle', 'Mark as read')}
              </ListItemText>
            </MenuItem>
          );
        })()}
      </Menu>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={downloadMenuOpen}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 10000,
          '& .MuiPaper-root': {
            minWidth: 150,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <MenuItem onClick={handleDownloadPdfClick}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('PDF İndir', 'Download PDF')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadHtmlClick}>
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('HTML İndir', 'Download HTML')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadUblClick}>
          <ListItemIcon>
            <ArticleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('UBL İndir', 'Download UBL')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* PavoPay Fatura Görüntüleme Modalı */}
      <Dialog
        open={pavoPayViewerOpen}
        onClose={() => { setPavoPayViewerOpen(false); setPavoPayInvoiceHtml(''); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={eInvoiceProvider === 'edm' ? 'EDM' : 'PavoPay'}
              size="small"
              color={eInvoiceProvider === 'edm' ? 'primary' : 'secondary'}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {pavoPayViewerInvoiceRef ? `${tt('Fatura', 'Invoice')}: ${pavoPayViewerInvoiceRef}` : tt('Fatura Önizleme', 'Invoice Preview')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setPavoPayViewerOpen(false); setPavoPayInvoiceHtml(''); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
          {pavoPayViewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{tt('Fatura yükleniyor...', 'Loading invoice...')}</Typography>
            </Box>
          ) : (
            <iframe
              srcDoc={pavoPayInvoiceHtml}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={tt('Fatura Önizleme', 'Invoice Preview')}
              sandbox="allow-same-origin allow-scripts"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PavoPay Uyarı Modalı */}
      <Dialog
        open={pavoPayBranchWarningOpen}
        onClose={() => setPavoPayBranchWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            {isManager ? tt('Şube Entegrasyonu Bulunamadı', 'Branch Integration Not Found') : tt('PavoPay Entegrasyonu Bulunamadı', 'PavoPay Integration Not Found')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isManager
              ? tt('Şubeniz için PavoPay entegrasyonu tanımlanmamış. Lütfen yöneticinizle iletişime geçin.', 'No PavoPay integration is defined for your branch. Please contact your administrator.')
              : tt('Bu şirket için genel (şubesiz) bir PavoPay entegrasyonu bulunmamaktadır. Mevcut PavoPay entegrasyonları şubelere özeldir ve e-fatura sayfasında yalnızca şube yöneticileri tarafından kullanılabilir. E-fatura işlemleri için şubesiz bir PavoPay entegrasyonu tanımlamanız gerekmektedir.', 'There is no global (branchless) PavoPay integration for this company. Existing PavoPay integrations are branch-specific and can only be used by branch managers on the e-invoice page. You need to define a branchless PavoPay integration for e-invoice operations.')
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPavoPayBranchWarningOpen(false)} variant="contained" size="small">
            {tt('Tamam', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Bildirimi */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === 'error' ? 6000 : 4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EInvoice;
